/**
 * De Hoppenaar — Biersommelier
 *
 * Stelt via een korte vragenlijst een persoonlijk bierpakket samen uit de
 * actuele voorraad van de webshop. Drie routes: beginner, liefhebber, kenner.
 * Optioneel gekoppeld aan Untappd om bieren weg te laten die de bezoeker al
 * gedronken heeft, en om smaakvoorkeuren af te leiden uit diens hoogst
 * beoordeelde bieren.
 *
 * Data:   /collections/<handle>?view=sommelier-data (templates/collection.sommelier-data.liquid)
 * Untappd: hergebruikt assets/untappd-user-filter.js (zelfde localStorage-state,
 *          dus "Al geproefd"-badges in de rest van de shop werken mee).
 * Winkelwagen: via de theme-module "cart-store" (incl. statiegeldregels).
 */
(function () {
  'use strict';

  // ===========================================================================
  // Kennisbank: bierstijlen
  // Smaakdimensies (0–1): zoet, bitter, zuur, rook (geroosterd), fruit, hop,
  // vol (body), funk (wild/brett), fris (licht & dorstlessend)
  // ===========================================================================
  var STYLES = {
    'lager':                 { fam: 'licht',    c: '#F2C14E', f: { fris: 1, bitter: .3, hop: .3, vol: .2 } },
    'india pale lager':      { fam: 'hop',      c: '#EDB63C', f: { fris: .7, hop: .8, bitter: .6, vol: .3 } },
    'pale ale':              { fam: 'hop',      c: '#E9A93A', f: { hop: .7, bitter: .5, fruit: .4, fris: .5, vol: .4 } },
    'ipa':                   { fam: 'hop',      c: '#E39B2D', f: { hop: .9, bitter: .7, fruit: .5, vol: .5 } },
    'west coast ipa':        { fam: 'hop',      c: '#E3952A', f: { hop: 1, bitter: 1, fruit: .4, fris: .3, vol: .4 } },
    'white ipa':             { fam: 'hop',      c: '#F0C76A', f: { hop: .6, fris: .6, fruit: .5, bitter: .4 } },
    'black ipa':             { fam: 'hop',      c: '#2B1A12', f: { hop: .8, bitter: .8, rook: .5, vol: .5 } },
    'double ipa':            { fam: 'hop',      c: '#E0952F', f: { hop: 1, fruit: .8, bitter: .6, vol: .7, zoet: .3 } },
    'triple ipa':            { fam: 'hop',      c: '#D98A2B', f: { hop: 1, fruit: .8, bitter: .6, vol: .9, zoet: .5 } },
    'quadruple ipa':         { fam: 'hop',      c: '#D3822A', f: { hop: 1, fruit: .8, bitter: .6, vol: 1, zoet: .6 } },
    'milkshake ipa':         { fam: 'hop',      c: '#F0B85A', f: { hop: .7, fruit: .8, zoet: .7, vol: .7, bitter: .3 } },
    'fruited ipa':           { fam: 'hop',      c: '#F2A65A', f: { hop: .7, fruit: 1, zoet: .4, vol: .5 } },
    'sour ipa':              { fam: 'hop',      c: '#F3B860', f: { hop: .7, zuur: .6, fruit: .7, fris: .5 } },
    'stout':                 { fam: 'donker',   c: '#1C110B', f: { rook: 1, zoet: .6, vol: .9, bitter: .4 } },
    'porter':                { fam: 'donker',   c: '#2E1A10', f: { rook: .8, zoet: .4, vol: .6, bitter: .3 } },
    'barleywine':            { fam: 'zwaar',    c: '#8A3E14', f: { zoet: .8, vol: 1, fruit: .4, bitter: .4 } },
    'wheatwine':             { fam: 'zwaar',    c: '#B5651D', f: { zoet: .8, vol: .9, fruit: .4 } },
    'old ale':               { fam: 'zwaar',    c: '#6B2F12', f: { zoet: .7, vol: .8, fruit: .4, rook: .3 } },
    'strong ale':            { fam: 'zwaar',    c: '#7A3512', f: { zoet: .7, vol: .8, fruit: .4, rook: .2 } },
    'strong dark ale':       { fam: 'belgisch', c: '#4A2311', f: { zoet: .7, vol: .8, fruit: .5, rook: .3 } },
    'winter ale':            { fam: 'zwaar',    c: '#5C2A12', f: { zoet: .7, vol: .7, rook: .3, fruit: .3 } },
    'eisbock':               { fam: 'zwaar',    c: '#3E1C0E', f: { zoet: .9, vol: 1, rook: .3 } },
    'quadrupel':             { fam: 'belgisch', c: '#5A2A12', f: { zoet: .7, vol: .9, fruit: .5, rook: .2 } },
    'dubbel':                { fam: 'belgisch', c: '#6A3216', f: { zoet: .6, vol: .6, fruit: .4, rook: .2 } },
    'tripel':                { fam: 'belgisch', c: '#E8B547', f: { zoet: .4, vol: .6, fruit: .5, bitter: .4, fris: .3 } },
    'blond':                 { fam: 'belgisch', c: '#F0C450', f: { fris: .6, zoet: .4, fruit: .3, vol: .4 } },
    'bokbier':               { fam: 'belgisch', c: '#6E3318', f: { zoet: .6, rook: .4, vol: .6 } },
    'witbier':               { fam: 'licht',    c: '#F6DC8A', f: { fris: 1, fruit: .5, zuur: .2 } },
    'weizen':                { fam: 'licht',    c: '#F2C96A', f: { fris: .8, fruit: .6, vol: .4 } },
    'radler':                { fam: 'licht',    c: '#F7E08C', f: { fris: 1, zoet: .7, fruit: .8 } },
    'red ale':               { fam: 'licht',    c: '#A3471E', f: { zoet: .5, vol: .5, bitter: .3 } },
    'rauchbier':             { fam: 'donker',   c: '#5B2B14', f: { rook: .8, vol: .5 } },
    'farmhouse ale / saison':{ fam: 'wild',     c: '#E7B451', f: { fris: .7, funk: .5, bitter: .4, fruit: .4 } },
    'sour':                  { fam: 'zuur',     c: '#E5795A', f: { zuur: 1, fruit: .6, fris: .7 } },
    'smoothie sour':         { fam: 'zuur',     c: '#D9466F', f: { zuur: .6, fruit: 1, zoet: .8, vol: .6 } },
    'fruitbier':             { fam: 'zuur',     c: '#C8344E', f: { fruit: 1, zoet: .6, zuur: .4, fris: .6 } },
    'lambiek':               { fam: 'wild',     c: '#C99A3B', f: { zuur: .9, funk: .9, fruit: .6 } },
    'geuze':                 { fam: 'wild',     c: '#E0B04A', f: { zuur: .9, funk: 1, fris: .6 } },
    'flanders red ale':      { fam: 'wild',     c: '#7E1F1A', f: { zuur: .7, zoet: .5, fruit: .6, funk: .4 } },
    'wild ale':              { fam: 'wild',     c: '#C98A3D', f: { zuur: .8, funk: .9, fruit: .6 } },
    'grape ale':             { fam: 'wild',     c: '#8E2A4A', f: { zuur: .6, fruit: .9, funk: .5 } },
    'italian grape ale':     { fam: 'wild',     c: '#8E2A4A', f: { zuur: .6, fruit: .9, funk: .5 } },
    'cider':                 { fam: 'cider',    c: '#E9C46A', f: { fruit: 1, fris: .9, zuur: .5, zoet: .4 } },
    'mede':                  { fam: 'cider',    c: '#E8A93A', f: { zoet: 1, fruit: .5, vol: .6 } },
    'alcoholvrij bier':      { fam: 'licht',    c: '#EFC860', f: { fris: .9, hop: .3 } }
  };
  var STYLE_ALIASES = {
    'neipa': 'double ipa', 'dipa': 'double ipa', 'tipa': 'triple ipa', 'imperial stout': 'stout',
    'kriek': 'lambiek', 'framboise': 'lambiek', 'gueuze': 'geuze', 'saison': 'farmhouse ale / saison',
    'pils': 'lager', 'pilsner': 'lager', 'quad': 'quadrupel', 'barley wine': 'barleywine', 'mead': 'mede'
  };

  var FAMILIES = {
    hop:      { label: 'IPA & hoppig',             c: '#E39B2D', desc: 'Pale ale, IPA, NEIPA, DIPA' },
    donker:   { label: 'Stout & porter',           c: '#1C110B', desc: 'Koffie, chocolade, geroosterd' },
    zuur:     { label: 'Zuur & fruitig',           c: '#D9466F', desc: 'Fruited sours, smoothie sours' },
    wild:     { label: 'Lambiek, geuze & wild',    c: '#C99A3B', desc: 'Spontaan vergist, funky, saison' },
    belgisch: { label: 'Belgisch',                 c: '#E8B547', desc: 'Blond, dubbel, tripel, quad' },
    zwaar:    { label: 'Barleywine & strong ale',  c: '#8A3E14', desc: 'Zwaar, warm, karamel & gedroogd fruit' },
    licht:    { label: 'Licht & fris',             c: '#F2C14E', desc: 'Lager, witbier, weizen' },
    cider:    { label: 'Cider & mede',             c: '#E9C46A', desc: 'Appel, peer, honing' }
  };

  var KEYWORDS = {
    koffie:    { re: /koffie|coffee|espresso|mocha|kaffe/, label: 'koffie' },
    vanille:   { re: /vanill/, label: 'vanille' },
    chocolade: { re: /chocola|cacao|cocoa|brownie|nib/, label: 'chocolade' },
    kokos:     { re: /kokos|coconut/, label: 'kokos' },
    karamel:   { re: /karamel|caramel|toffee|fudge|butterscotch|dulce/, label: 'karamel' },
    ahorn:     { re: /maple|ahorn/, label: 'ahornsiroop' },
    noten:     { re: /hazelno|hazelnut|pecan|almond|amandel|pinda|peanut|pistach|walnoot|walnut/, label: 'noten' },
    gebak:     { re: /pastry|cake|cookie|koek|taart|marshmallow|s'?mores|donut|pie\b|cheesecake|tiramisu|banket/, label: 'gebak' },
    citrus:    { re: /citrus|grapefruit|sinaas|orange|citroen|lemon|lime|yuzu|mandarijn/, label: 'citrus' },
    tropisch:  { re: /tropisch|tropical|mango|passievrucht|passion|ananas|pineapple|guave|guava|lychee|papaja/, label: 'tropisch fruit' },
    roodfruit: { re: /framboos|frambo|raspberr|kers|cherr|kriek|aardbei|strawberr|cassis|bramen|blackberr|blueberr|bosbes|cranberr/, label: 'rood fruit' },
    steenfruit:{ re: /perzik|peach|abrikoo|apricot|pruim|plum|nectarin/, label: 'steenfruit' },
    kruidig:   { re: /kaneel|cinnamon|chili|chipotle|peper|gember|ginger|speculaas|pepernot|kardemom|nootmuskaat/, label: 'kruiden' },
    bourbon:   { re: /bourbon|heaven hill|buffalo trace|weller|woodford|elijah craig|four roses|kentucky/, label: 'bourbonvat' },
    rye:       { re: /\brye\b|rogge/, label: 'ryevat' },
    whisky:    { re: /whisk|scotch|islay|laphroaig|ardbeg|lagavulin|bowmore|peat|turf|single malt/, label: 'whiskyvat' },
    wijn:      { re: /wijn|wine\b|wine barrel|\bport\b|porto|sherry|madeira|sauternes|cognac|brandy|calvados|armagnac|pinot|chardonnay/, label: 'wijn-/cognacvat' },
    rum:       { re: /\brum\b|tequila|mezcal|\bgin\b/, label: 'rum-/tequilavat' }
  };
  var ADDITIONS = ['koffie', 'vanille', 'chocolade', 'kokos', 'karamel', 'ahorn', 'noten', 'gebak', 'kruidig'];
  var BARREL_KW = ['bourbon', 'rye', 'whisky', 'wijn', 'rum'];

  var COUNTRY_GROUPS = {
    vs:     ['verenigde staten', 'amerika', 'usa', 'canada'],
    be:     ['belgië', 'belgie'],
    nl:     ['nederland'],
    noord:  ['noorwegen', 'zweden', 'denemarken', 'finland', 'estland', 'letland', 'litouwen', 'ijsland'],
    uk:     ['engeland', 'schotland', 'wales', 'ierland', 'verenigd koninkrijk', 'noord-ierland']
  };

  var THIS_YEAR = new Date().getFullYear();

  // ===========================================================================
  // Vragen
  // Elk antwoord heeft een `fx(profile)` die het smaakprofiel aanpast.
  // ===========================================================================
  function addF(p, dims) { for (var k in dims) p.flavor[k] = (p.flavor[k] || 0) + dims[k]; }
  function addFam(p, fams) { for (var k in fams) p.fam[k] = (p.fam[k] || 0) + fams[k]; }
  function addType(p, types) { for (var k in types) p.type[k] = (p.type[k] || 0) + types[k]; }
  function like(p) { for (var i = 1; i < arguments.length; i++) p.kwLike[arguments[i]] = 1; }

  var Q_PAKKET = function (level) {
    return {
      id: 'pakket', title: 'Hoe groot mag je pakket zijn?', type: 'single',
      help: 'Je kunt daarna nog bieren wisselen of verwijderen.',
      options: (level === 'kenner' ? [
        { v: 3, label: '3 bieren', desc: 'Een paar topflessen' },
        { v: 6, label: '6 bieren', desc: 'De gouden middenweg' },
        { v: 12, label: '12 bieren', desc: 'Kelder aanvullen' }
      ] : [
        { v: 4, label: '4 bieren', desc: 'Even kennismaken' },
        { v: 6, label: '6 bieren', desc: 'Een mooi proefpakket' },
        { v: 12, label: '12 bieren', desc: 'Ruim genoeg om te delen' }
      ]).map(function (o) { return { v: String(o.v), label: o.label, desc: o.desc, fx: function (p) { p.count = o.v; } }; })
    };
  };
  var Q_BUDGET = function (level) {
    var opts = level === 'kenner'
      ? [[1000, 'Tot €10'], [2000, 'Tot €20'], [3500, 'Tot €35'], [0, 'Geen limiet']]
      : level === 'liefhebber'
        ? [[600, 'Tot €6'], [1000, 'Tot €10'], [1800, 'Tot €18'], [0, 'Maakt niet uit']]
        : [[500, 'Tot €5'], [800, 'Tot €8'], [1200, 'Tot €12'], [0, 'Maakt niet uit']];
    return {
      id: 'budget', title: 'Wat mag één bier maximaal kosten?', type: 'single',
      options: opts.map(function (o) { return { v: String(o[0]), label: o[1], fx: function (p) { p.priceMax = o[0]; } }; })
    };
  };

  var ROUTES = {
    beginner: {
      label: 'Ik begin net', sub: 'Beginner',
      desc: 'Je weet nog niet precies wat je lekker vindt. We vragen naar smaken, niet naar bierstijlen.',
      questions: [
        { id: 'nu', title: 'Wat drink je nu het liefst?', type: 'single', options: [
          { v: 'pils', label: 'Pils of lager', desc: 'Fris en vertrouwd', fx: function (p) { addFam(p, { licht: 1, belgisch: .4, hop: .2 }); addF(p, { fris: 1 }); } },
          { v: 'fris', label: 'Iets fris-fruitigs', desc: 'Witbier, radler, cider', fx: function (p) { addFam(p, { licht: .6, zuur: .6, cider: .5 }); addF(p, { fris: .8, fruit: .8 }); } },
          { v: 'wijn', label: 'Wijn', desc: 'Rood, wit of bubbels', fx: function (p) { addFam(p, { wild: .7, zuur: .5, cider: .3 }); addF(p, { zuur: .5, fruit: .6 }); like(p, 'roodfruit'); } },
          { v: 'zoet', label: 'Cocktails of iets zoets', desc: 'Fruitig, zoet, makkelijk', fx: function (p) { addFam(p, { zuur: .8, hop: .2 }); addF(p, { zoet: 1, fruit: .8 }); } },
          { v: 'koffie', label: 'Koffie of pure chocolade', desc: 'Rijk en geroosterd', fx: function (p) { addFam(p, { donker: 1.2 }); addF(p, { rook: 1, zoet: .5 }); like(p, 'koffie', 'chocolade'); } },
          { v: 'niets', label: 'Nog niets bijzonders', desc: 'Ik sta overal voor open', fx: function (p) { addF(p, { fris: .3, fruit: .3 }); p.adventure += .8; } }
        ] },
        { id: 'smaak', title: 'Welke smaken spreken je aan?', help: 'Kies er maximaal drie.', type: 'multi', max: 3, options: [
          { v: 'tropisch', label: 'Citrus & tropisch fruit', desc: 'Mango, passievrucht, grapefruit', fx: function (p) { addFam(p, { hop: .8 }); addF(p, { fruit: .7, hop: .6 }); like(p, 'tropisch', 'citrus'); } },
          { v: 'rood', label: 'Rood fruit & zuurtjes', desc: 'Framboos, kers, bosvruchten', fx: function (p) { addFam(p, { zuur: .8, wild: .3 }); addF(p, { zuur: .6, fruit: .8 }); like(p, 'roodfruit'); } },
          { v: 'dessert', label: 'Koffie, chocolade & karamel', desc: 'Als een dessert in een glas', fx: function (p) { addFam(p, { donker: 1 }); addF(p, { rook: .7, zoet: .6 }); like(p, 'koffie', 'chocolade', 'karamel', 'vanille'); } },
          { v: 'honing', label: 'Honing & gedroogd fruit', desc: 'Rond, warm, licht zoet', fx: function (p) { addFam(p, { belgisch: 1, zwaar: .3, cider: .2 }); addF(p, { zoet: .6, vol: .4 }); } },
          { v: 'dorst', label: 'Fris & dorstlessend', desc: 'Licht, sprankelend', fx: function (p) { addFam(p, { licht: 1, belgisch: .2 }); addF(p, { fris: 1 }); } },
          { v: 'kruidig', label: 'Kruidig & bloemig', desc: 'Koriander, peper, gist', fx: function (p) { addFam(p, { belgisch: .6, wild: .3, licht: .3 }); like(p, 'kruidig'); } }
        ] },
        { id: 'bitter', title: 'Hoe sta je tegenover bitter?', help: 'Denk aan grapefruit of pure chocolade.', type: 'single', options: [
          { v: 'nee', label: 'Liever niet', fx: function (p) { addF(p, { bitter: -1.4 }); addType(p, { 'west coast ipa': -2, 'ipa': -.6 }); } },
          { v: 'beetje', label: 'Een beetje mag', fx: function (p) { addF(p, { bitter: -.2 }); } },
          { v: 'ja', label: 'Lekker juist!', fx: function (p) { addF(p, { bitter: 1, hop: .8 }); addFam(p, { hop: .5 }); } }
        ] },
        { id: 'zuur', title: 'En zuur in bier?', help: 'Zoals een zuurtje, kombucha of een frisse witte wijn.', type: 'single', options: [
          { v: 'nee', label: 'Nee, liever niet', fx: function (p) { addF(p, { zuur: -1.6, funk: -1.6 }); addFam(p, { zuur: -1, wild: -1.5 }); } },
          { v: 'misschien', label: 'Ik ben nieuwsgierig', fx: function (p) { addF(p, { zuur: .3, funk: -.5 }); } },
          { v: 'ja', label: 'Graag!', fx: function (p) { addF(p, { zuur: 1 }); addFam(p, { zuur: .7, wild: .3 }); } }
        ] },
        { id: 'sterkte', title: 'Licht of stevig?', type: 'single', options: [
          { v: 'licht', label: 'Licht', desc: 'Tot ongeveer 6%', fx: function (p) { p.abvMax = 6; } },
          { v: 'midden', label: 'Gemiddeld', desc: 'Tot ongeveer 9%', fx: function (p) { p.abvMax = 9; } },
          { v: 'stevig', label: 'Mag stevig zijn', desc: 'Ook 10% en hoger', fx: function (p) { addF(p, { vol: .4 }); } }
        ] },
        { id: 'moment', title: 'Waarvoor zoek je bier?', type: 'single', options: [
          { v: 'zelf', label: 'Om zelf te ontdekken', fx: function (p) { p.adventure += .5; } },
          { v: 'eten', label: 'Bij het eten', fx: function (p) { p.food = true; } },
          { v: 'vrienden', label: 'Gezellig met vrienden', fx: function (p) { addF(p, { fris: .3 }); } },
          { v: 'cadeau', label: 'Als cadeau', fx: function (p) { p.ratingWeight += 1.5; p.gift = true; } }
        ] },
        Q_PAKKET('beginner'),
        Q_BUDGET('beginner'),
        { id: 'untappd', type: 'untappd', optional: true, title: 'Gebruik je Untappd?', help: 'Optioneel. Dan laten we bieren weg die je al gedronken hebt.' }
      ]
    },

    liefhebber: {
      label: 'Ik drink regelmatig speciaalbier', sub: 'Liefhebber',
      desc: 'Je kent je favoriete stijlen. We verfijnen op hop, toevoegingen en vaten.',
      questions: [
        { id: 'untappd', type: 'untappd', title: 'Koppel je Untappd', help: 'Dan krijg je alleen bieren die je nog niet gecheckt hebt. Terwijl wij je profiel ophalen, beantwoord je de rest van de vragen.' },
        { id: 'stijlen', title: 'Welke stijlen drink je het liefst?', help: 'Kies er maximaal drie.', type: 'multi', max: 3,
          options: ['hop', 'donker', 'zuur', 'wild', 'belgisch', 'zwaar', 'licht', 'cider'].map(function (k) {
            return { v: k, label: FAMILIES[k].label, desc: FAMILIES[k].desc, swatch: FAMILIES[k].c, fx: function (p) { p.fam[k] = (p.fam[k] || 0) + 1.6; bucket(p, k, function (b) { return b.fam === k; }); } };
          }) },
        { id: 'hoptype', when: function (a) { return has(a.stijlen, 'hop'); }, title: 'Wat voor IPA?', type: 'single', options: [
          { v: 'hazy', label: 'Troebel & sappig', desc: 'NEIPA, DIPA, TIPA', fx: function (p) { addType(p, { 'double ipa': 1.5, 'triple ipa': 1.2, 'ipa': .6, 'milkshake ipa': .4, 'west coast ipa': -.6 }); addF(p, { fruit: .5 }); } },
          { v: 'wc', label: 'Helder & bitter', desc: 'West Coast, klassiek', fx: function (p) { addType(p, { 'west coast ipa': 2, 'ipa': .6, 'pale ale': .5, 'india pale lager': .5 }); addF(p, { bitter: .8 }); } },
          { v: 'fruit', label: 'Met fruit of zuur', desc: 'Fruited, milkshake, sour IPA', fx: function (p) { addType(p, { 'fruited ipa': 1.5, 'milkshake ipa': 1.2, 'sour ipa': 1.2 }); addF(p, { fruit: .6 }); } },
          { v: 'alles', label: 'Allemaal prima', fx: function () {} }
        ] },
        { id: 'donkertype', when: function (a) { return has(a.stijlen, 'donker'); }, title: 'Wat voor stout?', type: 'single', options: [
          { v: 'pastry', label: 'Pastry', desc: 'Zoet, met toevoegingen', fx: function (p) { like(p, 'vanille', 'gebak', 'karamel', 'kokos', 'ahorn'); addF(p, { zoet: .8 }); } },
          { v: 'klassiek', label: 'Klassiek', desc: 'Geroosterd, koffie, droog', fx: function (p) { like(p, 'koffie'); addF(p, { rook: .6, bitter: .3 }); p.kwAvoid.gebak = 1; } },
          { v: 'ba', label: 'Barrel aged', desc: 'Bourbon, whisky, eikenhout', fx: function (p) { p.baTypes.stout = 1; p.baTypes.porter = 1; } },
          { v: 'alles', label: 'Allemaal prima', fx: function () {} }
        ] },
        { id: 'zuurtype', when: function (a) { return has(a.stijlen, 'zuur') || has(a.stijlen, 'wild'); }, title: 'Wat voor zuur?', type: 'single', options: [
          { v: 'fruit', label: 'Fruitig & toegankelijk', desc: 'Fruited & smoothie sours', fx: function (p) { addType(p, { 'smoothie sour': 1.5, 'sour': 1, 'fruitbier': .8 }); } },
          { v: 'belgisch', label: 'Klassiek Belgisch', desc: 'Geuze, lambiek, kriek', fx: function (p) { addType(p, { 'geuze': 1.6, 'lambiek': 1.6, 'flanders red ale': .8 }); } },
          { v: 'wild', label: 'Wild & funky', desc: 'Wild ales, foeder, saison', fx: function (p) { addType(p, { 'wild ale': 1.6, 'farmhouse ale / saison': 1, 'grape ale': .8, 'italian grape ale': .8 }); addF(p, { funk: .8 }); } },
          { v: 'alles', label: 'Allemaal prima', fx: function () {} }
        ] },
        { id: 'toevoeging', title: 'Toevoegingen?', help: 'Kies wat je lekker vindt, of ga voor puur.', type: 'multi', max: 4, options: [
          { v: 'koffie', label: 'Koffie', fx: function (p) { like(p, 'koffie'); } },
          { v: 'vanille', label: 'Vanille & gebak', fx: function (p) { like(p, 'vanille', 'gebak'); } },
          { v: 'chocolade', label: 'Chocolade & karamel', fx: function (p) { like(p, 'chocolade', 'karamel'); } },
          { v: 'kokos', label: 'Kokos & noten', fx: function (p) { like(p, 'kokos', 'noten'); } },
          { v: 'fruit', label: 'Fruit', fx: function (p) { like(p, 'roodfruit', 'tropisch', 'steenfruit'); } },
          { v: 'kruiden', label: 'Kruiden & specerijen', fx: function (p) { like(p, 'kruidig'); } },
          { v: 'puur', label: 'Puur, zonder gimmicks', exclusive: true, fx: function (p) { ADDITIONS.forEach(function (k) { p.kwAvoid[k] = 1; }); } }
        ] },
        { id: 'vat', title: 'Barrel aged?', type: 'single', options: [
          { v: 'ja', label: 'Ja, graag', fx: function (p) { p.barrel = 'ja'; } },
          { v: 'soms', label: 'Af en toe', fx: function (p) { if (p.barrel !== 'ja') p.barrel = 'soms'; } },
          { v: 'nee', label: 'Liever niet', fx: function (p) { p.barrel = 'nee'; } }
        ] },
        { id: 'abv', title: 'Hoe zwaar mag het zijn?', type: 'single', options: [
          { v: 'tot7', label: 'Tot 7%', fx: function (p) { p.abvMax = 7; } },
          { v: '7-10', label: '7 tot 10%', fx: function (p) { p.abvMin = 7; p.abvMax = 10; } },
          { v: '10plus', label: '10% en hoger', fx: function (p) { p.abvMin = 10; } },
          { v: 'alles', label: 'Maakt niet uit', fx: function () {} }
        ] },
        { id: 'avontuur', title: 'Vertrouwd of verrassend?', type: 'single', options: [
          { v: 'vertrouwd', label: 'Binnen mijn stijlen', desc: 'Geef me meer van wat ik ken', fx: function (p) { p.adventure = .4; p.famBoost = 1.3; } },
          { v: 'mix', label: 'Vooral vertrouwd, beetje nieuw', fx: function (p) { p.adventure = 1.4; } },
          { v: 'verras', label: 'Verras me', desc: 'Ook stijlen buiten mijn comfortzone', fx: function (p) { p.adventure = 3; p.famBoost = .6; } }
        ] },
        Q_PAKKET('liefhebber'),
        Q_BUDGET('liefhebber')
      ]
    },

    kenner: {
      label: 'Untappd is mijn tweede thuis', sub: 'Kenner',
      desc: 'Je jaagt op specifieke stijlen, vaten en jaargangen. We filteren scherp en laten weg wat je al hebt gehad.',
      questions: [
        { id: 'untappd', type: 'untappd', title: 'Koppel je Untappd', help: 'We laten je gecheckte bieren weg en lezen je hoogst beoordeelde bieren om je smaak te leren kennen.' },
        { id: 'jacht', title: 'Waar jaag je op?', help: 'Kies er maximaal vier.', type: 'multi', max: 4, options: [
          { v: 'bastout', label: 'BA Imperial Stout', swatch: '#1C110B', fx: function (p) { addType(p, { stout: 1.8, porter: .6 }); p.baTypes.stout = 1; p.baTypes.porter = 1; bucket(p, 'bastout', function (b) { return isType(b, 'stout', 'porter') && b.ba; }); } },
          { v: 'pastry', label: 'Pastry Stout', swatch: '#3B2214', fx: function (p) { addType(p, { stout: 1.6 }); like(p, 'vanille', 'gebak', 'karamel', 'kokos', 'ahorn', 'noten'); bucket(p, 'pastry', function (b) { return isType(b, 'stout', 'porter') && (b.kw.gebak || b.kw.vanille || b.kw.karamel || b.kw.kokos || b.kw.ahorn || b.kw.noten); }); } },
          { v: 'barleywine', label: 'Barleywine & Wheatwine', swatch: '#8A3E14', fx: function (p) { addType(p, { barleywine: 2, wheatwine: 2, 'old ale': 1, 'strong ale': .8 }); bucket(p, 'barleywine', function (b) { return isType(b, 'barleywine', 'wheatwine', 'old ale', 'strong ale'); }); } },
          { v: 'quad', label: 'Quadrupel & Belgisch sterk', swatch: '#5A2A12', fx: function (p) { addType(p, { quadrupel: 2, 'strong dark ale': 1.5, dubbel: .8, tripel: .6 }); bucket(p, 'quad', function (b) { return isType(b, 'quadrupel', 'strong dark ale', 'dubbel', 'tripel'); }); } },
          { v: 'geuze', label: 'Geuze & Lambiek', swatch: '#E0B04A', fx: function (p) { addType(p, { geuze: 2, lambiek: 2 }); bucket(p, 'geuze', function (b) { return isType(b, 'geuze', 'lambiek'); }); } },
          { v: 'wild', label: 'Wild Ale & Foeder', swatch: '#C98A3D', fx: function (p) { addType(p, { 'wild ale': 2, 'flanders red ale': 1.2, 'farmhouse ale / saison': 1, 'grape ale': 1, 'italian grape ale': 1 }); bucket(p, 'wild', function (b) { return isType(b, 'wild ale', 'flanders red ale', 'farmhouse ale / saison', 'grape ale', 'italian grape ale'); }); } },
          { v: 'sour', label: 'Fruited & Smoothie Sour', swatch: '#D9466F', fx: function (p) { addType(p, { 'smoothie sour': 2, sour: 1.4, fruitbier: 1 }); bucket(p, 'sour', function (b) { return isType(b, 'smoothie sour', 'sour', 'fruitbier'); }); } },
          { v: 'hazy', label: 'DIPA, TIPA & QIPA', swatch: '#E0952F', fx: function (p) { addType(p, { 'double ipa': 1.8, 'triple ipa': 2, 'quadruple ipa': 2 }); bucket(p, 'hazy', function (b) { return isType(b, 'double ipa', 'triple ipa', 'quadruple ipa'); }); } },
          { v: 'wc', label: 'West Coast IPA', swatch: '#E3952A', fx: function (p) { addType(p, { 'west coast ipa': 2.2, ipa: .6 }); bucket(p, 'wc', function (b) { return isType(b, 'west coast ipa', 'ipa'); }); } },
          { v: 'cider', label: 'Cider, mede & grape ale', swatch: '#E9C46A', fx: function (p) { addType(p, { cider: 2, mede: 2, 'grape ale': 1.2, 'italian grape ale': 1.2 }); bucket(p, 'cider', function (b) { return isType(b, 'cider', 'mede', 'grape ale', 'italian grape ale'); }); } },
          { v: 'extreem', label: 'Eisbock & extreem (14%+)', swatch: '#3E1C0E', fx: function (p) { addType(p, { eisbock: 2.2 }); p.extreme = true; bucket(p, 'extreem', function (b) { return isType(b, 'eisbock') || b.abv >= 14; }); } }
        ] },
        { id: 'vatk', title: 'Barrel aged?', help: 'Op vaten gerijpt bier: bourbon, whisky, wijn …', type: 'single', options: [
          { v: 'ja', label: 'Ja, graag', desc: 'Liefst op vat gerijpt', fx: function (p) { p.barrel = 'ja'; } },
          { v: 'mag', label: 'Mag, hoeft niet', fx: function (p) { if (p.barrel !== 'ja') p.barrel = 'neutraal'; } },
          { v: 'nee', label: 'Liever niet', desc: 'Behalve als je hierboven BA stout koos', fx: function (p) { p.barrel = 'nee'; } }
        ] },
        { id: 'vattype', when: function (a) { return a.vatk === 'ja' || a.vatk === 'mag' || has(a.jacht, 'bastout'); }, title: 'Welke vaten?', help: 'Telt alleen mee voor barrel aged bieren.', type: 'multi', max: 3, options: [
          { v: 'bourbon', label: 'Bourbon', fx: function (p) { p.barrelKw.bourbon = 1; } },
          { v: 'rye', label: 'Rye & whiskey', fx: function (p) { p.barrelKw.rye = 1; p.barrelKw.whisky = .6; } },
          { v: 'whisky', label: 'Islay & Scotch', fx: function (p) { p.barrelKw.whisky = 1; } },
          { v: 'wijn', label: 'Wijn, port & cognac', fx: function (p) { p.barrelKw.wijn = 1; } },
          { v: 'rum', label: 'Rum, tequila & exotisch', fx: function (p) { p.barrelKw.rum = 1; } },
          { v: 'alles', label: 'Maakt niet uit', exclusive: true, fx: function () {} }
        ] },
        { id: 'jaargang', title: 'Vers of gerijpt?', type: 'single', options: [
          { v: 'vers', label: 'Zo vers mogelijk', fx: function (p) { p.vintage = 'vers'; } },
          { v: 'oud', label: 'Gerijpt waar het past', desc: 'Stouts, barleywines, quads en lambiek mogen jaren oud zijn; IPA\'s altijd vers', fx: function (p) { p.vintage = 'oud'; } },
          { v: 'alles', label: 'Maakt niet uit', fx: function () {} }
        ] },
        { id: 'score', title: 'Minimale Untappd-score?', type: 'single', options: [
          { v: '0', label: 'Geen minimum', fx: function () {} },
          { v: '3.75', label: '3,75+', fx: function (p) { p.minRating = 3.75; } },
          { v: '4', label: '4,00+', fx: function (p) { p.minRating = 4; } },
          { v: '4.25', label: '4,25+', desc: 'Alleen het topsegment', fx: function (p) { p.minRating = 4.25; } }
        ] },
        { id: 'herkomst', title: 'Herkomst?', type: 'multi', max: 5, options: [
          { v: 'vs', label: 'Verenigde Staten & Canada', fx: function (p) { p.countries.vs = 1; } },
          { v: 'be', label: 'België', fx: function (p) { p.countries.be = 1; } },
          { v: 'noord', label: 'Scandinavië & Baltische staten', fx: function (p) { p.countries.noord = 1; } },
          { v: 'nl', label: 'Nederland', fx: function (p) { p.countries.nl = 1; } },
          { v: 'uk', label: 'VK & Ierland', fx: function (p) { p.countries.uk = 1; } },
          { v: 'alles', label: 'Maakt niet uit', exclusive: true, fx: function () {} }
        ] },
        { id: 'schaars', title: 'Zeldzaamheid', type: 'single', options: [
          { v: 'ja', label: 'Laat me de schaarse flessen zien', desc: 'Laatste exemplaren eerst', fx: function (p) { p.rare = true; } },
          { v: 'nee', label: 'Maakt niet uit', fx: function () {} }
        ] },
        Q_PAKKET('kenner'),
        Q_BUDGET('kenner')
      ]
    },

    proeverij: {
      label: 'Proeverij voor groepen', sub: 'Proeverij', group: true,
      desc: 'Organiseer een bierproeverij thuis, met vrienden of op het werk. Wij rekenen uit hoeveel je nodig hebt en zetten de bieren in proefvolgorde.',
      questions: [
        { id: 'groep', type: 'number', min: 2, max: 60, def: 10, unit: 'personen', title: 'Met hoeveel personen proef je?', help: 'We rekenen met 15 cl per persoon per bier en kijken of er genoeg op voorraad is.' },
        { id: 'publiek', title: 'Wie schuiven er aan?', type: 'single', options: [
          { v: 'gemengd', label: 'Een gemengd gezelschap', desc: 'Van pilsdrinker tot liefhebber', fx: function (p) { p.abvMax = 11; p.adventure = 1.2; } },
          { v: 'beginners', label: 'Vooral beginners', desc: 'Toegankelijk, maar wel verrassend', fx: function (p) { p.abvMax = 9; p.noWild = true; addF(p, { fris: .4, bitter: -.4, funk: -.8 }); } },
          { v: 'liefhebbers', label: 'Liefhebbers', desc: 'Mag uitgesproken zijn', fx: function (p) { p.abvMax = 13; p.ratingWeight = 2.2; } },
          { v: 'kenners', label: 'Echte kenners', desc: 'Topbieren en zeldzaamheden', fx: function (p) { p.ratingWeight = 3; p.adventure = 1.5; } }
        ] },
        { id: 'thema', title: 'Welk thema kies je?', type: 'single', options: [
          { v: 'rondje', label: 'Rondje langs de stijlen', desc: 'Van licht en fris naar zwaar en donker', fx: function (p) { p.theme = 'rondje'; } },
          { v: 'hop', label: 'Alles over hop', desc: 'Pale ale, IPA, West Coast, DIPA', fx: function (p) { p.theme = 'hop'; addFam(p, { hop: 3 }); } },
          { v: 'donker', label: 'Donker & zwaar', desc: 'Porter, stout, quad, barleywine', fx: function (p) { p.theme = 'donker'; addFam(p, { donker: 2, zwaar: 1.6, belgisch: .6 }); } },
          { v: 'zuur', label: 'Zuur & wild', desc: 'Sours, lambiek, geuze, wild ale', fx: function (p) { p.theme = 'zuur'; addFam(p, { zuur: 2, wild: 2 }); } },
          { v: 'belgisch', label: 'Belgisch', desc: 'Blond, dubbel, tripel, quad, geuze', fx: function (p) { p.theme = 'belgisch'; p.countryOnly = 'be'; addFam(p, { belgisch: 2.2, wild: 1 }); } },
          { v: 'nl', label: 'Nederlandse brouwers', desc: 'Het beste van eigen bodem', fx: function (p) { p.theme = 'rondje'; p.countryOnly = 'nl'; } }
        ] },
        { id: 'soorten', title: 'Hoeveel bieren wil je proeven?', help: 'Zes is een mooie avond; acht voor de doorzetters.', type: 'single', options: [
          [4, 'Kort en krachtig'], [5, ''], [6, 'Onze aanrader'], [7, ''], [8, 'Een complete avond']
        ].map(function (o) { return { v: String(o[0]), label: o[0] + ' bieren', desc: o[1], fx: function (p) { p.count = o[0]; } }; }) },
        { id: 'notities', title: 'Proefnotities erbij?', help: 'Eén vel per persoon met ruimte voor kleur, geur, smaak, afdronk en een score. Downloaden is altijd gratis.', type: 'single', options: [
          { v: 'druk', label: 'Gedrukt meesturen', desc: 'Klein bedrag per persoon, zit in het pakket', fx: function (p) { p.printNotes = true; } },
          { v: 'zelf', label: 'Ik print ze zelf', desc: 'Gratis te downloaden bij je advies', fx: function (p) { p.printNotes = false; } }
        ] }
      ],
      finalize: function (prof, answers) {
        prof.group = clampGroup(answers.groep);
        prof.pour = POUR_CL;
        prof.minPer = MIN_PER_KIND;
        prof.excludeTasted = false;
        prof.ignoreTasted = true;
        if (!prof.theme || prof.theme === 'rondje') {
          // Elke soort uit een andere stijlfamilie; de belangrijkste families eerst
          var order = ['hop', 'donker', 'zuur', 'belgisch', 'licht', 'wild', 'zwaar', 'cider'];
          if (prof.noWild) order = ['hop', 'donker', 'zuur', 'belgisch', 'licht', 'cider', 'zwaar', 'wild'];
          order.slice(0, prof.count).forEach(function (k) {
            prof.fam[k] = (prof.fam[k] || 0) + 1.2;
            bucket(prof, k, function (b) { return b.fam === k; });
          });
        }
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Proeverij voor groepen
  // ---------------------------------------------------------------------------
  var POUR_CL = 15;        // proefglas per persoon per bier
  var MIN_PER_KIND = 4;    // minimaal per soort, nodig voor de proeverijkorting
  var MAX_KINDS = 8;
  function clampGroup(v) { var n = Math.round(Number(v) || 10); return Math.max(2, Math.min(60, n)); }
  /** Aantal blikken/flessen per soort: groep × 15 cl, naar boven afgerond, minimaal 4. */
  function unitsFor(p, prof) {
    var need = Math.ceil(prof.group * (prof.pour || POUR_CL) / (p.cl || 33) - 1e-6);
    return Math.max(prof.minPer || MIN_PER_KIND, need);
  }
  // Proefvolgorde: licht en fris eerst, zwaar en donker als laatste
  var TASTE_ORDER = { licht: 0, cider: 1, hop: 2, zuur: 3, wild: 4, overig: 5, belgisch: 5, donker: 6, zwaar: 7 };
  function tastingSort(a, b) {
    var pa = a.p, pb = b.p;
    var fa = TASTE_ORDER[pa.fam] != null ? TASTE_ORDER[pa.fam] : 5, fb = TASTE_ORDER[pb.fam] != null ? TASTE_ORDER[pb.fam] : 5;
    return (fa * 4 + (pa.abv || 6) / 3) - (fb * 4 + (pb.abv || 6) / 3);
  }
  function groupCalc(n) {
    var cl = n * POUR_CL;
    return n + ' × ' + POUR_CL + ' cl = <strong>' + liters(cl) + ' liter per bier</strong> · bij blikken van 44 cl zijn dat er ' +
      Math.max(MIN_PER_KIND, Math.ceil(cl / 44 - 1e-6)) + ' per soort';
  }
  function liters(cl) { return (Math.round(cl / 10) / 10).toFixed(1).replace('.', ','); }
  /** Korte beschrijving: eerste zinnen uit de productomschrijving. */
  function shortDesc(t, max) {
    t = String(t || '').replace(/\s*Over (dit|deze) bier\s*/gi, ' ').replace(/\s+/g, ' ').trim();
    if (t.length <= max) return t;
    var cut = t.slice(0, max);
    var dot = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
    return dot > max * .45 ? cut.slice(0, dot + 1) : cut.replace(/\s+\S*$/, '') + '…';
  }

  function has(arr, v) { return Array.isArray(arr) && arr.indexOf(v) !== -1; }
  function isType(p) { var a = arguments; for (var i = 1; i < a.length; i++) if (p.type === a[i]) return true; return false; }
  function bucket(p, id, test) { p.buckets.push({ id: id, test: test }); }
  // Stijlen die mooi rijpen; IPA's en fruitige sours horen vers.
  var AGEABLE = { donker: 1, zwaar: 1, belgisch: 1, wild: 1 };

  function newProfile(level) {
    return {
      level: level, flavor: {}, fam: {}, type: {}, kwLike: {}, kwAvoid: {}, barrelKw: {},
      barrel: 'neutraal', baTypes: {}, buckets: [], abvMin: 0, abvMax: 0, priceMax: 0, minRating: 0, countries: {},
      vintage: '', rare: false, count: 6, adventure: level === 'beginner' ? 1.2 : 1.4,
      famBoost: 1, ratingWeight: level === 'kenner' ? 2.5 : 1.8, gift: false, food: false,
      excludeTasted: true, untappdStyles: {}
    };
  }

  // ===========================================================================
  // Catalogus
  // ===========================================================================
  var CACHE_KEY = 'hoppenaar:sommelier:catalog:v1';
  var CACHE_TTL = 30 * 60 * 1000;

  function loadCatalog(url) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var c = JSON.parse(raw);
        if (c && c.url === url && Date.now() - c.at < CACHE_TTL && c.products && c.products.length) {
          return Promise.resolve(c.products.map(normalize));
        }
      }
    } catch (e) { /* geen cache */ }

    function page(n) {
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      return fetch(url + sep + 'page=' + n, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (t) { return JSON.parse(t); });
    }
    return page(1).then(function (first) {
      var pages = Math.min(Number(first.pages) || 1, 20);
      var rest = [];
      for (var i = 2; i <= pages; i++) rest.push(page(i));
      return Promise.all(rest).then(function (others) {
        var all = first.products.slice();
        others.forEach(function (o) { all = all.concat(o.products || []); });
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ url: url, at: Date.now(), products: all })); } catch (e) { /* te groot of privé */ }
        return all.map(normalize);
      });
    });
  }

  function resolveStyle(raw) {
    var t = (raw.y || '').toLowerCase().trim();
    if (!STYLES[t] || t.indexOf('bier -') === 0) {
      var s = (raw.s || '').toLowerCase().trim();
      if (STYLES[s]) t = s;
      else if (STYLE_ALIASES[s]) t = STYLE_ALIASES[s];
      else {
        t = '';
        var tags = (raw.g || []).map(function (x) { return String(x).toLowerCase(); });
        for (var i = 0; i < tags.length; i++) {
          if (STYLES[tags[i]]) { t = tags[i]; break; }
          if (STYLE_ALIASES[tags[i]]) { t = STYLE_ALIASES[tags[i]]; break; }
        }
      }
    }
    return t;
  }

  function normalize(raw) {
    var key = resolveStyle(raw);
    var st = STYLES[key] || { fam: 'overig', c: '#C9A25B', f: {} };
    var text = ((raw.t || '') + ' ' + (raw.x || '') + ' ' + (raw.g || []).join(' ')).toLowerCase();
    var titleTags = ((raw.t || '') + ' ' + (raw.g || []).join(' ')).toLowerCase();
    var kw = {};
    for (var k in KEYWORDS) {
      // Vat-woorden alleen uit titel/tags: beschrijvingen noemen vaak vaten die
      // niet van toepassing zijn ("geen vat, wel ...").
      var src = BARREL_KW.indexOf(k) !== -1 ? titleTags : text;
      if (KEYWORDS[k].re.test(src)) kw[k] = 1;
    }
    var ba = raw.ba === true || raw.ba === 'true' || /barrel aged|\bba\b|barrel-aged|vatgerijpt|barrel/.test(titleTags);
    var year = Number(raw.j) || 0;
    if (!year) { var m = String(raw.t || '').match(/\b(20[0-3]\d)\b/); if (m) year = Number(m[1]); }
    var abv = Number(raw.a) || 0;
    var f = Object.assign({}, st.f);
    if (abv >= 11) f.vol = Math.min(1, (f.vol || 0) + .3);
    if (abv && abv <= 5.5) f.fris = Math.min(1, (f.fris || 0) + .3);
    if (kw.gebak || kw.vanille || kw.karamel || kw.ahorn) f.zoet = Math.min(1, (f.zoet || 0) + .3);
    if (kw.roodfruit || kw.tropisch || kw.steenfruit) f.fruit = Math.min(1, (f.fruit || 0) + .3);
    var land = String(raw.l || '').toLowerCase();
    var cgroup = '';
    for (var g in COUNTRY_GROUPS) if (COUNTRY_GROUPS[g].indexOf(land) !== -1) cgroup = g;
    return {
      id: raw.id, handle: raw.h, title: raw.t, vendor: raw.v || '', type: key, typeLabel: raw.s || raw.y || '',
      fam: st.fam, color: st.c, f: f, kw: kw, ba: ba, abv: abv, land: raw.l || '', cgroup: cgroup,
      cl: Number(raw.c) || 0, year: year, price: Number(raw.p) || 0, vid: raw.vid, qty: Number(raw.q) || 0,
      deposit: Number(raw.d) || 0, rating: Number(raw.r) || 0, bid: raw.u ? String(raw.u) : '', url: raw.uu || '',
      img: raw.i || '', isNew: (raw.g || []).indexOf('Nieuw') !== -1, desc: raw.x || ''
    };
  }

  // ===========================================================================
  // Aanbevelingsengine
  // ===========================================================================
  function rng(seed) {
    return function () {
      seed |= 0; seed = seed + 0x6D2B79F5 | 0;
      var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function scoreAll(products, prof, tasted, seed) {
    var rand = rng(seed);
    var out = [];
    var famTotal = 0; for (var fk in prof.fam) if (prof.fam[fk] > 0) famTotal += prof.fam[fk];
    var hasTypePref = Object.keys(prof.type).some(function (k) { return prof.type[k] > 0; });
    var barrelKwChosen = Object.keys(prof.barrelKw).length > 0;
    var countriesChosen = Object.keys(prof.countries).length > 0;

    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (!p.vid || !p.price) continue;
      var isTasted = !prof.ignoreTasted && !!(p.bid && tasted && tasted.has(p.bid));
      if (isTasted && prof.excludeTasted) continue;
      if (prof.priceMax && p.price > prof.priceMax) continue;
      if (p.abv && prof.abvMax && p.abv > prof.abvMax + (prof.level === 'beginner' ? 1 : .5)) continue;
      if (p.abv && prof.abvMin && p.abv < prof.abvMin - .5) continue;
      if (prof.barrel === 'nee' && p.ba && prof.level !== 'beginner' && !prof.baTypes[p.type]) continue;
      if (prof.countryOnly && p.cgroup !== prof.countryOnly) continue;
      var need = 0;
      if (prof.group) {
        // Genoeg op voorraad voor de hele groep? Alcoholvrij en losse cadeausets niet.
        need = unitsFor(p, prof);
        if (p.qty < need) continue;
        if (p.abv && p.abv < 1) continue;
        if (p.fam === 'overig') continue;
      }

      var s = 0, why = [];

      // Stijlfamilie
      var fw = prof.fam[p.fam] || 0;
      if (fw) {
        var famScore = fw * 2.2 * prof.famBoost;
        s += famScore;
        if (fw > 0.9 && FAMILIES[p.fam]) why.push({ w: famScore, t: FAMILIES[p.fam].label });
      } else if (famTotal > 0) {
        s -= 1.2 * prof.famBoost;
      }

      // Specifieke stijl
      var tw = prof.type[p.type] || 0;
      if (tw) { s += tw * 2.2; if (tw > 0) why.push({ w: tw * 2.2 + 1, t: 'Jouw stijl: ' + (p.typeLabel || p.type) }); }
      else if (hasTypePref && prof.level === 'kenner') s -= 2.5;

      // Smaakprofiel
      var fs = 0;
      for (var d in prof.flavor) fs += prof.flavor[d] * (p.f[d] || 0);
      s += fs * 1.6;

      // Smaakwoorden
      var hits = [];
      for (var k in prof.kwLike) if (p.kw[k] && ADDITIONS.concat(['citrus', 'tropisch', 'roodfruit', 'steenfruit']).indexOf(k) !== -1) { s += 1.4; hits.push(KEYWORDS[k].label); }
      for (var a in prof.kwAvoid) if (p.kw[a]) s -= 2.5;
      if (hits.length) why.push({ w: 1.4 * hits.length + .5, t: 'Met ' + joinNl(hits.slice(0, 3)) });

      // Vaten
      if (p.ba) {
        var wantBa = prof.barrel === 'ja' || prof.baTypes[p.type];
        if (wantBa) { s += 2.2; }
        else if (prof.barrel === 'soms') { s += .4; }
        else if (prof.barrel === 'nee') { s -= 3; }
        var bhit = null;
        if (wantBa || prof.barrel !== 'nee') {
          for (var b in prof.barrelKw) if (p.kw[b]) { s += (wantBa ? 1.6 : .8) * prof.barrelKw[b]; bhit = KEYWORDS[b].label; }
          if (barrelKwChosen && !bhit && wantBa) s -= .8;
        }
        if (wantBa || bhit) why.push({ w: 2.3, t: bhit ? 'Gerijpt op ' + bhit : 'Barrel aged' });
      } else if (prof.barrel === 'ja' && (p.fam === 'donker' || p.fam === 'zwaar')) {
        s -= 1.5;
      }

      // Alcohol (alcoholvrij alleen als iemand licht wil)
      if (p.abv && p.abv < 1 && !(prof.abvMax && prof.abvMax <= 6)) s -= 2.5;
      if (p.abv) {
        if (prof.abvMax && p.abv > prof.abvMax) s -= 1.2 * (p.abv - prof.abvMax);
        if (prof.abvMin && p.abv < prof.abvMin) s -= 1.2 * (prof.abvMin - p.abv);
      }

      // Untappd-score
      if (p.rating) {
        s += (p.rating - 3.75) * prof.ratingWeight;
        if (prof.minRating && p.rating < prof.minRating) s -= 4;
        if (p.rating >= 4.1) why.push({ w: (p.rating - 3.75) * prof.ratingWeight, t: 'Untappd ' + p.rating.toFixed(2).replace('.', ',') });
      } else if (prof.minRating) {
        s -= 1.5;
      }

      // Herkomst
      if (countriesChosen) {
        if (p.cgroup && prof.countries[p.cgroup]) s += 1.6; else s -= .8;
      }

      // Jaargang
      if (p.year) {
        var ageable = AGEABLE[p.fam];
        if (prof.vintage === 'oud' && ageable && p.year <= THIS_YEAR - 2) { s += 2; why.push({ w: 2, t: 'Jaargang ' + p.year + ', mooi gerijpt' }); }
        if (prof.vintage === 'vers' && p.year < THIS_YEAR - 1) s -= 1.8;
        // Hoppig en fruitig bier is altijd op z'n best vers
        if (!ageable && p.year < THIS_YEAR) s -= 2;
      }

      // Schaarste
      if (prof.rare) {
        if (p.qty > 0 && p.qty <= 3) { s += 1.8; why.push({ w: 1.8, t: 'Nog maar ' + p.qty + ' op voorraad' }); }
      }

      // Untappd-smaakprofiel (hoogst beoordeelde bieren van de bezoeker)
      var us = prof.untappdStyles[p.type];
      if (us) { s += us; if (us >= .8) why.push({ w: us + .2, t: 'Lijkt op bieren die je hoog scoorde' }); }

      if (prof.extreme && p.abv >= 14) { s += 2.5; why.push({ w: 2.5, t: abvLabel(p.abv) + ' — niet voor watjes' }); }

      if (isTasted) s -= 2.5;
      if (p.isNew) s += .3;
      if (prof.gift && p.cl >= 37.5) s += .4;

      // Budget is een plafond, geen doel: iets duurder mag, maar niet standaard het maximum
      if (prof.priceMax) s -= (p.price / prof.priceMax) * 1.6;
      else s -= Math.min(p.price / 4000, 1.5) * .8;

      if (prof.group) {
        // Grote flessen zijn duur voor een proefglas; kosten per persoon tellen mee
        if (p.cl > 50) s -= 1.2;
        s -= Math.min(p.price * need / 100 / prof.group, 8) * .3;
      }

      s += rand() * prof.adventure;

        // Stijlreden en familiereden niet allebei tonen
      if (tw > 0 && fw > 0.9) why = why.filter(function (x) { return !(FAMILIES[p.fam] && x.t === FAMILIES[p.fam].label); });
      why.sort(function (x, y) { return y.w - x.w; });
      out.push({ p: p, s: s, why: why.slice(0, 3).map(function (x) { return x.t; }), tasted: isTasted });
    }
    out.sort(function (x, y) { return y.s - x.s; });
    return out;
  }

  function pick(ranked, count, prof, keep) {
    var chosen = (keep || []).slice();
    var used = {}; chosen.forEach(function (c) { used[c.p.id] = 1; });
    var famChosen = Object.keys(prof.fam).filter(function (k) { return prof.fam[k] > 0.9; }).length;
    var perType = prof.group ? 1 : prof.level === 'kenner' ? Math.max(2, Math.ceil(count / 2)) : Math.max(1, Math.ceil(count / 3));
    var perVendor = prof.group ? 1 : count >= 10 ? 3 : 2;
    var pool = ranked.slice(0, 600);
    var buckets = (prof.buckets || []);
    var quota = buckets.length ? Math.ceil(count / buckets.length) : 0;
    function fills() { return buckets.map(function (bk) { return chosen.filter(function (c) { return bk.test(c.p); }).length; }); }
    while (chosen.length < count) {
      var fill = fills();
      var best = null, bestS = -Infinity;
      for (var i = 0; i < pool.length; i++) {
        var c = pool[i];
        if (used[c.p.id]) continue;
        var sameV = 0, sameT = 0, sameF = 0, sameName = false;
        for (var j = 0; j < chosen.length; j++) {
          var q = chosen[j].p;
          if (q.vendor === c.p.vendor) sameV++;
          if (q.type === c.p.type) sameT++;
          if (q.fam === c.p.fam) sameF++;
          if (baseName(q.title) === baseName(c.p.title)) sameName = true;
        }
        if (sameName) continue;
        var eff = c.s - sameV * 1.6 - (sameT >= perType ? 3 : sameT * .6);
        if (sameV >= perVendor) eff -= 4;
        if (buckets.length > 1) {
          // Verdeel het pakket eerlijk over de gekozen stijlen
          var minFill = Infinity;
          for (var bi = 0; bi < buckets.length; bi++) if (buckets[bi].test(c.p)) minFill = Math.min(minFill, fill[bi]);
          if (minFill === Infinity) eff -= 2.5;
          else if (minFill >= quota) eff -= 4;
          else eff += (quota - minFill) * 1.2;
        } else if (famChosen > 1) eff -= sameF * .5;
        if (eff > bestS) { bestS = eff; best = c; }
      }
      if (!best) break;
      used[best.p.id] = 1;
      chosen.push(best);
    }
    return chosen;
  }

  // "Brouwerij - Bier (2024)" en "Brouwerij - Bier (2025)" zijn hetzelfde bier.
  function baseName(t) { return String(t || '').toLowerCase().replace(/\(.*?\)/g, '').replace(/\b20\d\d\b/g, '').replace(/\s+/g, ' ').trim(); }

  function persona(prof, chosen) {
    var famCount = {};
    chosen.forEach(function (c) { famCount[c.p.fam] = (famCount[c.p.fam] || 0) + 1; });
    var top = Object.keys(famCount).sort(function (a, b) { return famCount[b] - famCount[a]; })[0] || 'overig';
    var share = chosen.length ? famCount[top] / chosen.length : 0;
    var baShare = chosen.filter(function (c) { return c.p.ba; }).length / (chosen.length || 1);
    if (prof.level === 'kenner' && baShare >= .5) return { name: 'De Vatenjager', line: 'Eikenhout, bourbon en geduld: jij weet wat tijd met bier doet.' };
    if (share < .45) return { name: 'De Ontdekker', line: 'Je houdt van variatie. Dit pakket neemt je mee langs verschillende stijlen.' };
    var map = {
      hop: { name: 'De Hopzoeker', line: 'Sappig, fruitig of lekker bitter: hop is jouw ding.' },
      donker: { name: 'De Donkere Genieter', line: 'Koffie, chocolade en geroosterde mout. Bier om voor te gaan zitten.' },
      zuur: { name: 'De Zuurliefhebber', line: 'Fris, fruitig en met een lekker zuurtje.' },
      wild: { name: 'De Wilde Fermentator', line: 'Spontane gisting, funk en complexiteit. Bier als wijn.' },
      belgisch: { name: 'De Bourgondiër', line: 'Rond, warm en vol karakter, zoals de Belgen het bedoeld hebben.' },
      zwaar: { name: 'De Doordrinker', line: 'Zwaar, rijk en verwarmend. Perfect voor lange avonden.' },
      licht: { name: 'De Dorstlesser', line: 'Fris en toegankelijk, maar wel met karakter.' },
      cider: { name: 'De Fruitkenner', line: 'Appel, peer en honing, net buiten de gebaande paden.' }
    };
    return map[top] || { name: 'De Ontdekker', line: 'Een gevarieerd pakket om je smaak verder te ontdekken.' };
  }

  function joinNl(arr) {
    if (arr.length <= 1) return arr.join('');
    return arr.slice(0, -1).join(', ') + ' & ' + arr[arr.length - 1];
  }

  // ===========================================================================
  // Untappd
  // ===========================================================================
  var UT_KEY = 'hoppenaar:untappd-user-filter';
  var UT_TASTE_KEY = 'hoppenaar:sommelier:untappd-taste';
  var PROXY = function (url) { return 'https://api.codetabs.com/v1/proxy/?quest=' + url; };

  function utState() {
    try { var s = JSON.parse(localStorage.getItem(UT_KEY) || 'null'); if (s && Array.isArray(s.beerIds)) return s; } catch (e) { /* */ }
    return { username: '', beerIds: [], breweryCount: 0, purchasedCount: 0, fetchedAt: 0 };
  }
  function utSave(s) { try { localStorage.setItem(UT_KEY, JSON.stringify(s)); } catch (e) { /* */ } }

  /** Start de bestaande Untappd-scrape. Gebruikt bij voorkeur het footer-widget,
   *  zodat cache, status en "Al geproefd"-badges in de hele shop meeliften. */
  function utConnect(username, onProgress) {
    var clean = String(username || '').trim().replace(/^@/, '');
    if (!/^[A-Za-z0-9_.-]{1,40}$/.test(clean)) return Promise.reject(new Error('invalid-username'));

    var widget = document.querySelector('[data-untappd-user-filter]');
    var form = widget && widget.querySelector('[data-untappd-user-filter-form]');
    var input = widget && widget.querySelector('[data-untappd-user-filter-input]');
    var statusEl = widget && widget.querySelector('[data-untappd-user-filter-status]');

    if (form && input && window.__untappdUserFilter) {
      var before = utState();
      if (before.username.toLowerCase() !== clean.toLowerCase()) utSave({ username: '', beerIds: [], breweryCount: 0, purchasedCount: 0, fetchedAt: 0 });
      input.value = clean;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true }));
      return new Promise(function (resolve, reject) {
        var started = Date.now(), lastCount = -1, stableTicks = 0;
        var timer = setInterval(function () {
          var s = utState();
          var statusText = statusEl ? statusEl.textContent : '';
          var isErr = statusEl && statusEl.dataset.error === 'true';
          if (isErr && !s.beerIds.length) { clearInterval(timer); reject(new Error(statusText || 'fetch-failed')); return; }
          var sameUser = s.username && s.username.toLowerCase() === clean.toLowerCase();
          var count = sameUser ? s.beerIds.length : 0;
          var busy = /%|achtergrond|Bezig/.test(statusText);
          if (onProgress) onProgress({ count: count, busy: busy, text: statusText });
          if (count === lastCount) stableTicks++; else stableTicks = 0;
          lastCount = count;
          if (sameUser && count > 0 && !busy && stableTicks >= 1) { clearInterval(timer); resolve(s); return; }
          if (Date.now() - started > 4 * 60 * 1000) { clearInterval(timer); if (count) resolve(s); else reject(new Error('timeout')); }
        }, 1200);
      });
    }

    // Fallback zonder footer-widget: zelf de scrape-functies aanroepen.
    var api = window.__untappdUserFilter;
    if (!api || !api.scrapeMain) return Promise.reject(new Error('unavailable'));
    return api.scrapeMain(clean).then(function (main) {
      if (main.error) throw new Error(main.error);
      var s = { username: main.username, beerIds: main.beerIds.map(String), breweryCount: main.breweryIds.length, purchasedCount: 0, fetchedAt: Date.now() };
      utSave(s);
      if (onProgress) onProgress({ count: s.beerIds.length, busy: true });
      return api.scrapeAllFilters(main.username, main.breweryIds, main.styleIds, main.countryIds, s.beerIds, function (batch) {
        s.beerIds = batch.ids; utSave(s);
        if (onProgress) onProgress({ count: s.beerIds.length, busy: true, text: Math.round(batch.done / batch.total * 100) + '%' });
      }).then(function () { s.fetchedAt = Date.now(); utSave(s); if (api.applyToAll) api.applyToAll(); return s; });
    });
  }

  /** Haalt de 25 hoogst beoordeelde bieren van de gebruiker op en zet die om
   *  in een stijlvoorkeur. Mislukt stil: het is een extraatje. */
  function utFetchTaste(username) {
    var clean = String(username || '').trim().replace(/^@/, '');
    try {
      var cached = JSON.parse(localStorage.getItem(UT_TASTE_KEY) || 'null');
      if (cached && cached.username === clean.toLowerCase() && Date.now() - cached.at < 7 * 864e5) return Promise.resolve(cached.styles);
    } catch (e) { /* */ }
    var url = 'https://untappd.com/user/' + encodeURIComponent(clean) + '/beers?sort=highest_rated_you';
    return fetch(PROXY(url), { credentials: 'omit' }).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
      var styles = parseTaste(html);
      try { localStorage.setItem(UT_TASTE_KEY, JSON.stringify({ username: clean.toLowerCase(), at: Date.now(), styles: styles })); } catch (e) { /* */ }
      return styles;
    }).catch(function () { return {}; });
  }

  function parseTaste(html) {
    var out = {};
    if (!html || html.length < 2000) return out;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var items = doc.querySelectorAll('.beer-item');
    items.forEach(function (it) {
      var styleEl = it.querySelector('.style');
      var style = styleEl ? styleEl.textContent : '';
      var txt = it.textContent || '';
      var m = txt.match(/Their Rating \(([\d.]+)\)/i) || txt.match(/Rating \(([\d.]+)\)/i);
      var rating = m ? Number(m[1]) : 0;
      if (!rating) {
        var caps = it.querySelector('.you [data-rating], .you .caps[data-rating]');
        if (caps) rating = Number(caps.getAttribute('data-rating')) || 0;
      }
      addTaste(out, style, rating || 4);
    });
    return finishTaste(out);
  }

  function addTaste(acc, untappdStyle, rating) {
    var key = mapUntappdStyle(untappdStyle);
    if (!key) return;
    var w = rating >= 4.25 ? 1 : rating >= 4 ? .7 : rating >= 3.75 ? .3 : rating < 3.25 ? -.4 : 0;
    acc[key] = (acc[key] || 0) + w;
  }
  function finishTaste(acc) {
    var max = 0; for (var k in acc) max = Math.max(max, acc[k]);
    var out = {};
    if (max > 0) for (var k2 in acc) out[k2] = Math.round(Math.max(-1, acc[k2] / max * 2) * 100) / 100;
    return out;
  }

  function mapUntappdStyle(s) {
    s = String(s || '').toLowerCase();
    if (!s) return '';
    if (/stout/.test(s)) return 'stout';
    if (/porter/.test(s)) return 'porter';
    if (/barleywine|barley wine/.test(s)) return 'barleywine';
    if (/wheat wine|wheatwine/.test(s)) return 'wheatwine';
    if (/eisbock/.test(s)) return 'eisbock';
    if (/quadrupel|quad/.test(s)) return 'quadrupel';
    if (/tripel/.test(s)) return 'tripel';
    if (/dubbel/.test(s)) return 'dubbel';
    if (/belgian strong dark/.test(s)) return 'strong dark ale';
    if (/old ale|strong ale|english strong|scotch ale|wee heavy/.test(s)) return 'strong ale';
    if (/gueuze|geuze/.test(s)) return 'geuze';
    if (/lambic|kriek|framboise/.test(s)) return 'lambiek';
    if (/flanders/.test(s)) return 'flanders red ale';
    if (/grape ale/.test(s)) return 'grape ale';
    if (/wild ale|brett/.test(s)) return 'wild ale';
    if (/saison|farmhouse/.test(s)) return 'farmhouse ale / saison';
    if (/sour - smoothie|smoothie|pastry sour/.test(s)) return 'smoothie sour';
    if (/ipa - sour/.test(s)) return 'sour ipa';
    if (/sour|gose|berliner/.test(s)) return 'sour';
    if (/fruit beer/.test(s)) return 'fruitbier';
    if (/ipa - quadruple/.test(s)) return 'quadruple ipa';
    if (/ipa - triple/.test(s)) return 'triple ipa';
    if (/ipa - milkshake/.test(s)) return 'milkshake ipa';
    if (/ipa - black/.test(s)) return 'black ipa';
    if (/ipa - white/.test(s)) return 'white ipa';
    if (/ipa - imperial|double|ipa - new england \/ hazy imperial|imperial \/ double/.test(s) && /ipa/.test(s)) return 'double ipa';
    if (/west coast|ipa - american|ipa - english/.test(s)) return 'west coast ipa';
    if (/ipa/.test(s)) return 'ipa';
    if (/pale ale/.test(s)) return 'pale ale';
    if (/lager|pilsner|pils|helles|kellerbier/.test(s)) return 'lager';
    if (/witbier|wheat beer - wit/.test(s)) return 'witbier';
    if (/hefeweizen|weizen|wheat/.test(s)) return 'weizen';
    if (/blonde|belgian blond/.test(s)) return 'blond';
    if (/bock/.test(s)) return 'bokbier';
    if (/cider|perry/.test(s)) return 'cider';
    if (/mead/.test(s)) return 'mede';
    if (/red ale/.test(s)) return 'red ale';
    if (/rauch|smoked/.test(s)) return 'rauchbier';
    return '';
  }

  /** Untappd-export (Insider): JSON (lijst check-ins) of CSV. */
  function parseExport(text) {
    var rows = [];
    var trimmed = String(text || '').trim();
    if (trimmed[0] === '[' || trimmed[0] === '{') {
      var data = JSON.parse(trimmed);
      rows = Array.isArray(data) ? data : (data.checkins || data.items || []);
    } else {
      rows = parseCsv(trimmed);
    }
    var ids = new Set(), acc = {};
    rows.forEach(function (r) {
      var bid = r.bid || r.beer_id || (r.beer && r.beer.bid);
      if (bid) ids.add(String(bid));
      var style = r.beer_type || r.beer_style || (r.beer && r.beer.beer_style);
      var rating = Number(r.rating_score || r.rating || 0);
      if (style && rating) addTaste(acc, style, rating);
    });
    return { ids: Array.from(ids), styles: finishTaste(acc), rows: rows.length };
  }

  function parseCsv(text) {
    var lines = [], row = [], field = '', q = false;
    for (var i = 0; i < text.length; i++) {
      var ch = text[i];
      if (q) {
        if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (ch === '"') q = false;
        else field += ch;
      } else if (ch === '"') q = true;
      else if (ch === ',') { row.push(field); field = ''; }
      else if (ch === '\n' || ch === '\r') {
        if (ch === '\r' && text[i + 1] === '\n') i++;
        row.push(field); field = ''; lines.push(row); row = [];
      } else field += ch;
    }
    if (field || row.length) { row.push(field); lines.push(row); }
    if (!lines.length) return [];
    var head = lines[0].map(function (h) { return h.trim().toLowerCase(); });
    return lines.slice(1).filter(function (l) { return l.length > 1; }).map(function (l) {
      var o = {}; head.forEach(function (h, idx) { o[h] = l[idx]; }); return o;
    });
  }

  // ===========================================================================
  // Winkelwagen
  // ===========================================================================
  function depositConfig() {
    var el = document.getElementById('cart-deposit-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Herkomst-labels (verborgen voor de klant door de underscore), zodat in bestellingen
  // te zien is welke bieren via de biersommelier zijn gekocht.
  function sourceProps(route) {
    return { 'properties[_bron]': 'biersommelier', 'properties[_sommelier_route]': route || '' };
  }
  function withSource(item, route) { var sp = sourceProps(route); for (var k in sp) item[k] = sp[k]; return item; }

  function buildCartItems(beers, route, qtys) {
    var dep = depositConfig();
    var items = [];
    beers.forEach(function (p) {
      var n = (qtys && qtys[p.id]) || 1;
      var depVariant = p.deposit > 0 && dep && dep.variantsByAmount ? dep.variantsByAmount[String(p.deposit)] : null;
      if (p.deposit > 0 && depVariant) {
        var gid = 'deposit-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
        items.push(withSource({ id: p.vid, quantity: n, 'properties[_deposit_group_id]': gid, 'properties[_deposit_role]': 'main', 'properties[_deposit_cents]': String(p.deposit) }, route));
        items.push({ id: depVariant, quantity: n, 'properties[_deposit_group_id]': gid, 'properties[_deposit_role]': 'deposit', 'properties[_deposit_cents]': String(p.deposit), 'properties[Type]': 'Statiegeld', 'properties[Voor]': p.title });
      } else {
        items.push(withSource({ id: p.vid, quantity: n }, route));
      }
    });
    return items;
  }

  // Toevoegen gaat rechtstreeks via de Shopify Ajax API (werkt in elke browser).
  // Daarna laten we de theme-winkelwagen (cart-store) verversen, zodat het
  // winkelwagenicoon, de drawer en de statiegeldcontrole meteen meedoen.
  function toAjaxItems(items) {
    return items.map(function (it) {
      var o = { id: it.id, quantity: it.quantity };
      Object.keys(it).forEach(function (k) {
        var m = k.match(/^properties\[(.+)\]$/);
        if (m) { o.properties = o.properties || {}; o.properties[m[1]] = it[k]; }
      });
      return o;
    });
  }

  function postItems(items) {
    return fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: toAjaxItems(items) })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (res) {
        if (!r.ok || res.status) throw new Error(res.description || res.message || ('HTTP ' + r.status));
        return res;
      });
    });
  }

  function loadCartStore() {
    try {
      if (typeof window.importShim === 'function') return window.importShim('cart-store');
      return import('cart-store');
    } catch (e) {
      return Promise.reject(e);
    }
  }

  function refreshThemeCart() {
    return loadCartStore().then(function (mod) {
      var Cart = mod && mod.default;
      if (!Cart || typeof Cart.refresh !== 'function') throw new Error('no cart store');
      return Cart.refresh();
    }).then(function () { return true; }, function () { return false; });
  }

  function addToCart(beers, route, qtys, extra) {
    var groups = beers.map(function (p) { return buildCartItems([p], route, qtys); });
    var all = [].concat.apply([], groups);
    return postItems(all).then(function () {
      return { added: beers.length, failed: [] };
    }, function () {
      // Eén bier uitverkocht of beperkt? Dan per bier proberen, zodat de rest wel lukt.
      var failed = [], added = 0, chain = Promise.resolve();
      groups.forEach(function (g, i) {
        chain = chain.then(function () {
          return postItems(g).then(function () { added++; }, function () { failed.push(beers[i].title); });
        });
      });
      return chain.then(function () {
        if (!added) throw new Error(failed.length ? 'deze bieren zijn net uitverkocht of niet meer beschikbaar' : 'onbekende fout');
        return { added: added, failed: failed };
      });
    }).then(function (res) {
      return (extra ? extra(res) : Promise.resolve()).then(function () { return res; });
    }).then(function (res) {
      return refreshThemeCart().then(function (synced) { res.synced = synced; return res; });
    });
  }

  function cartRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function postJson(path, body) {
    return fetch(cartRoot() + path, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  }
  /** Proeverij: kortingscode toepassen en groepsinfo + tegoed op de bestelling zetten. */
  function applyGroupExtras(code, attributes) {
    return postJson('cart/update.js', { attributes: attributes, discount: code }).then(function (cart) {
      var ok = cart && cart.discount_codes && cart.discount_codes.some(function (d) { return String(d.code).toUpperCase() === code.toUpperCase(); });
      // Oudere checkout: code via de klassieke /discount-link als cookie zetten
      if (!ok) return fetch(cartRoot() + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(cartRoot() + 'cart.js'), { credentials: 'same-origin' }).catch(function () {});
    }).catch(function () {
      return fetch(cartRoot() + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(function () {});
    });
  }

  function openCart() {
    var el = document.getElementById('modal-cart') || document.getElementById('popup-cart-notification');
    try { if (el && typeof el.show === 'function') { el.show(); return true; } } catch (e) { /* */ }
    return false;
  }

  // ===========================================================================
  // UI
  // ===========================================================================
  function money(cents) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function abvLabel(a) { return a ? String(a).replace('.', ',') + '%' : ''; }
  function clLabel(c) { return c ? String(c).replace('.', ',') + ' cl' : ''; }

  // ---------------------------------------------------------------------------
  // Proefnotities (printbaar, ook op te slaan als PDF via de printdialoog)
  // ---------------------------------------------------------------------------
  function notesDoc(beers, opts) {
    opts = opts || {};
    var slots = beers && beers.length ? beers : new Array(opts.slots || 6).fill(null);
    var date = new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
    function stars() { return '<span class="stars">☆ ☆ ☆ ☆ ☆</span>'; }
    function field(label) { return '<div class="f"><span class="l">' + label + '</span><span class="ln"></span></div>'; }
    var blocks = slots.map(function (p, i) {
      var head = p
        ? '<div class="bh"><span class="no">' + (i + 1) + '</span><div><p class="v">' + esc(p.vendor) + '</p><h3>' + esc(p.title) + '</h3>' +
          '<p class="m">' + esc([p.typeLabel, abvLabel(p.abv), p.land].filter(Boolean).join(' · ')) + '</p></div></div>' +
          (p.desc ? '<p class="d">' + esc(shortDesc(p.desc, 330)) + '</p>' : '')
        : '<div class="bh"><span class="no">' + (i + 1) + '</span><div class="blank"><span class="l">Bier</span><span class="ln"></span><span class="l">Brouwerij · stijl · %</span><span class="ln"></span></div></div>';
      return '<section class="beer">' + head +
        '<div class="grid">' + field('Kleur & schuim') + field('Geur') + field('Smaak') + field('Mondgevoel') + field('Afdronk') +
        '<div class="f"><span class="l">Mijn score</span>' + stars() + '</div></div>' +
        '<div class="f full"><span class="l">Notities</span><span class="ln"></span><span class="ln"></span></div>' +
      '</section>';
    }).join('');
    var intro = beers && beers.length
      ? '<p class="sub">Proeverij' + (opts.group ? ' voor ' + opts.group + ' personen' : '') + (opts.thema ? ' · ' + esc(opts.thema) : '') + ' · ' + date + '</p>' +
        '<ol class="list">' + beers.map(function (p) { return '<li><strong>' + esc(p.title) + '</strong> <span>' + esc([p.typeLabel, abvLabel(p.abv)].filter(Boolean).join(' · ')) + '</span></li>'; }).join('') + '</ol>'
      : '<p class="sub">Naam ____________________________ &nbsp; Datum ________________</p>';
    var tip = '<p class="tip"><strong>Zo proef je:</strong> schenk ± 15 cl, bekijk kleur en schuim, ruik eerst kort en dan nog eens, neem een slok en laat hem even in je mond. Water en een stukje brood tussendoor houden je smaak scherp. Proef van licht naar zwaar.</p>';
    var credit = opts.credit && opts.group
      ? '<div class="credit"><strong>Zin in meer?</strong> Bij dit pakket hoort een tegoed van ' + money(opts.credit) + ' per persoon op een begeleide proeverij in onze speciaalbierwinkel in Arnhem of Grave. Noem je ordernummer bij het boeken.</div>' : '';
    var css = '@page{size:A4;margin:14mm}*{box-sizing:border-box}body{margin:0;font-family:"Archivo Narrow",Arial,sans-serif;color:#212121;font-size:12.5px;line-height:1.45;background:#f2f2f2}' +
      '.page{max-width:820px;margin:0 auto;padding:28px 34px;background:#fff}h1,h3,.l,.no,.btn{font-family:Oswald,Arial,sans-serif;text-transform:uppercase;letter-spacing:1px;font-weight:400}' +
      'h1{margin:0;font-size:30px;color:#26432a}h1::after{content:"";display:block;width:36px;height:2px;background:#385a3c;margin:10px 0}.brand{margin:0;font-family:Oswald,Arial;letter-spacing:2px;text-transform:uppercase;color:#385a3c;font-size:12px}' +
      '.sub{margin:0 0 10px;color:#555}.list{margin:6px 0 14px;padding-left:20px;columns:2;column-gap:28px}.list li{break-inside:avoid;margin-bottom:3px}.list span{color:#777}' +
      '.tip{background:#f2f2f2;border-left:3px solid #e1b462;padding:9px 12px;margin:0 0 16px;font-size:12px}' +
      '.beer{break-inside:avoid;border:1px solid #d9d9d9;border-radius:8px;padding:12px 14px;margin-bottom:12px}.bh{display:flex;gap:12px;align-items:flex-start}' +
      '.no{flex:0 0 32px;height:32px;border-radius:50%;background:#26432a;color:#e1b462;display:flex;align-items:center;justify-content:center;font-size:15px;letter-spacing:0}' +
      '.v{margin:0;color:#777;font-size:11px;text-transform:uppercase;letter-spacing:1px}h3{margin:1px 0 2px;font-size:15px;text-transform:none;letter-spacing:0;font-family:"Archivo Narrow",Arial;font-weight:700}.m{margin:0;color:#555}.d{margin:8px 0 0;color:#333}' +
      '.blank{flex:1;display:grid;grid-template-columns:auto 1fr;gap:6px 10px;align-items:end}.grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 22px;margin-top:10px}' +
      '.f{display:flex;align-items:flex-end;gap:8px;min-height:24px}.f.full{flex-wrap:wrap;margin-top:4px}.f.full .ln{flex-basis:100%;min-height:20px}.l{flex:0 0 auto;font-size:10.5px;color:#385a3c}.ln{flex:1;border-bottom:1px solid #bbb;min-height:18px}' +
      '.stars{font-size:17px;letter-spacing:3px;color:#c99a45}.credit{margin-top:14px;padding:12px 14px;border-radius:8px;background:#26432a;color:#fff}.credit strong{color:#e1b462}' +
      '.foot{margin-top:14px;font-size:11px;color:#777;text-align:center}.bar{position:sticky;top:0;display:flex;gap:10px;justify-content:center;padding:12px;background:#26432a}' +
      '.btn{border:0;border-radius:25px;padding:10px 24px;background:#e1b462;color:#274636;font-size:13px;cursor:pointer}.btn.alt{background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5)}' +
      '@media print{body{background:#fff}.bar{display:none}.page{padding:0;max-width:none}}';
    return '<!doctype html><html lang="nl"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">' +
      '<title>Proefnotities · De Hoppenaar</title>' +
      '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo+Narrow:wght@400;700&family=Oswald:wght@400&display=swap">' +
      '<style>' + css + '</style></head><body>' +
      '<div class="bar"><button class="btn" onclick="window.print()">Afdrukken of opslaan als PDF</button><button class="btn alt" onclick="window.close()">Sluiten</button></div>' +
      '<div class="page"><p class="brand">De Hoppenaar</p><h1>Proefnotities</h1>' + intro + tip + blocks + credit +
      '<p class="foot">De Hoppenaar · speciaalbier in Arnhem, Grave en op hoppenaar.nl</p></div></body></html>';
  }

  function openNotes(beers, opts) {
    var html = notesDoc(beers, opts);
    var w = null;
    try { w = window.open('', '_blank'); } catch (e) { /* popup geblokkeerd */ }
    if (w && w.document) { w.document.open(); w.document.write(html); w.document.close(); return; }
    // Popup geblokkeerd: als bestand downloaden
    var a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    a.download = 'proefnotities-de-hoppenaar.html';
    document.body.appendChild(a); a.click(); a.remove();
  }

  var STORE_KEY = 'hoppenaar:sommelier:last';

  function Sommelier(root) {
    this.root = root;
    this.cfg = {
      dataUrl: root.getAttribute('data-data-url'),
      minOrder: Number(root.getAttribute('data-min-order')) || 2500,
      freeShipping: Number(root.getAttribute('data-free-shipping')) || 8000,
      heading: root.getAttribute('data-heading') || 'Biersommelier',
      lead: root.getAttribute('data-lead') || '',
      img: {
        beginner: root.getAttribute('data-img-beginner') || '',
        liefhebber: root.getAttribute('data-img-liefhebber') || '',
        kenner: root.getAttribute('data-img-kenner') || '',
        proeverij: root.getAttribute('data-img-proeverij') || '',
        result: root.getAttribute('data-img-result') || ''
      },
      group: {
        code: root.getAttribute('data-group-code') || 'PROEVERIJ10',
        pct: Number(root.getAttribute('data-group-pct') || 10),
        credit: Number(root.getAttribute('data-group-credit') || 500),
        notesVariant: root.getAttribute('data-notes-variant') || '',
        notesPrice: Number(root.getAttribute('data-notes-price') || 50)
      }
    };
    this.state = { level: null, step: 0, answers: {}, seed: (Date.now() % 100000) | 0 };
    this.products = null;
    this.tasted = new Set();
    this.untappdStyles = {};
    this.ut = { status: 'idle', count: 0, text: '', username: '' };
    this.selection = [];
    this.ranked = [];

    var s = utState();
    if (s.beerIds.length) {
      this.tasted = new Set(s.beerIds.map(String));
      this.ut = { status: 'done', count: s.beerIds.length, username: s.username };
      var self = this;
      utFetchTaste(s.username).then(function (st) { self.untappdStyles = st || {}; });
    }

    this.catalogPromise = loadCatalog(this.cfg.dataUrl).then(function (p) { return p; });
    var me = this;
    this.catalogPromise.then(function (p) { me.products = p; }).catch(function (e) { me.catalogError = e; });

    root.addEventListener('click', this.onClick.bind(this));
    root.addEventListener('change', this.onChange.bind(this));
    root.addEventListener('input', this.onInput.bind(this));
    root.addEventListener('submit', this.onSubmit.bind(this));
    this.renderIntro();
  }

  Sommelier.prototype.route = function () { return ROUTES[this.state.level]; };
  Sommelier.prototype.visibleQuestions = function () {
    var a = this.state.answers;
    return this.route().questions.filter(function (q) { return !q.when || q.when(a); });
  };

  Sommelier.prototype.view = function (html, focusSel) {
    this.root.querySelector('[data-bs-stage]').innerHTML = html;
    var target = this.root.querySelector(focusSel || '[data-bs-focus]');
    if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
    var top = this.root.getBoundingClientRect().top;
    if (top < 0) this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  Sommelier.prototype.renderIntro = function () {
    var last = null;
    try { last = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { /* */ }
    var imgs = this.cfg.img;
    var g = this.cfg.group;
    var cards = Object.keys(ROUTES).filter(function (k) { return !ROUTES[k].group; }).map(function (k, i) {
      var r = ROUTES[k];
      var n = r.questions.length;
      var bg = imgs[k] ? ' style="--img:url(\'' + esc(imgs[k]) + '\')"' : '';
      return '<button type="button" class="bs-level" data-bs-level="' + k + '"' + bg + '>' +
        '<span class="bs-level__tag">' + esc(r.sub) + '</span>' +
        '<span class="bs-level__title">' + esc(r.label) + '</span>' +
        '<span class="bs-level__desc">' + esc(r.desc) + '</span>' +
        '<span class="bs-level__cta">Start</span>' +
        '<span class="bs-level__meta">' + n + ' korte vragen' + (k !== 'beginner' ? ' · met Untappd' : '') + '</span>' +
        '</button>';
    }).join('');
    this.view(
      '<div class="bs-intro">' +
        '<p class="bs-eyebrow">De Hoppenaar</p>' +
        '<h2 class="bs-title">' + esc(this.cfg.heading) + '</h2>' +
        (this.cfg.lead ? '<p class="bs-lead">' + esc(this.cfg.lead) + '</p>' : '') +
        '<p class="bs-intro__ask">Hoe goed ken jij speciaalbier?</p>' +
        '<div class="bs-levels">' + cards + '</div>' +
        '<div class="bs-group">' +
          '<button type="button" class="bs-level bs-level--wide" data-bs-level="proeverij"' + (imgs.proeverij || imgs.result ? ' style="--img:url(\'' + esc(imgs.proeverij || imgs.result) + '\')"' : '') + '>' +
            '<span class="bs-level__tag">Voor groepen</span>' +
            '<span class="bs-level__title">' + esc(ROUTES.proeverij.label) + '</span>' +
            '<span class="bs-level__desc">' + esc(ROUTES.proeverij.desc) + '</span>' +
            '<span class="bs-level__perks">' +
              '<span>' + g.pct + '% korting vanaf ' + MIN_PER_KIND + ' per soort</span>' +
              '<span>Voorraad per bier gecheckt</span>' +
              '<span>' + money(g.credit) + ' p.p. tegoed op een proeverij in Arnhem of Grave</span>' +
            '</span>' +
            '<span class="bs-level__cta">Stel je proeverij samen</span>' +
          '</button>' +
          '<p class="bs-group__blank"><button type="button" class="bs-link bs-link--caps" data-bs-blank>Blanco proefnotities downloaden (gratis)</button></p>' +
        '</div>' +
        (last && last.level && ROUTES[last.level] ? '<p class="bs-resume"><button type="button" class="bs-link bs-link--caps" data-bs-resume>Bekijk je vorige advies opnieuw</button></p>' : '') +
      '</div>'
    );
  };

  Sommelier.prototype.renderQuestion = function () {
    var qs = this.visibleQuestions();
    var q = qs[this.state.step];
    if (!q) return this.renderResult();
    var total = qs.length;
    var pct = Math.round((this.state.step) / total * 100);
    var ans = this.state.answers[q.id];
    var body;

    if (q.type === 'untappd') body = this.untappdHtml(q);
    else if (q.type === 'number') {
      var val = clampGroup(ans || q.def);
      body = '<form class="bs-num" data-bs-num-form novalidate>' +
          '<button type="button" class="bs-num__btn" data-bs-num="-1" aria-label="Eén minder">−</button>' +
          '<label class="visually-hidden" for="bs-num">' + esc(q.title) + '</label>' +
          '<input id="bs-num" class="bs-input bs-num__input" type="number" inputmode="numeric" min="' + q.min + '" max="' + q.max + '" value="' + val + '">' +
          '<button type="button" class="bs-num__btn" data-bs-num="1" aria-label="Eén meer">+</button>' +
          '<span class="bs-num__unit">' + esc(q.unit || '') + '</span>' +
        '</form>' +
        '<p class="bs-num__calc" data-bs-num-calc>' + groupCalc(val) + '</p>' +
        '<div class="bs-actions"><button type="button" class="btn btn--primary c-btn c-btn--primary bs-next" data-bs-next>Volgende</button></div>';
    } else {
      var opts = q.options.map(function (o) {
        var sel = q.type === 'multi' ? has(ans, o.v) : ans === o.v;
        return '<button type="button" class="bs-option' + (sel ? ' is-selected' : '') + '" data-bs-option="' + esc(o.v) + '" aria-pressed="' + (sel ? 'true' : 'false') + '">' +
          (o.swatch ? '<span class="bs-option__swatch" style="--sw:' + o.swatch + '"></span>' : '') +
          '<span class="bs-option__text"><span class="bs-option__label">' + esc(o.label) + '</span>' +
          (o.desc ? '<span class="bs-option__desc">' + esc(o.desc) + '</span>' : '') + '</span>' +
          (q.type === 'multi' ? '<span class="bs-option__check" aria-hidden="true"></span>' : '') +
          '</button>';
      }).join('');
      body = '<div class="bs-options bs-options--' + (q.options.length > 6 ? 'many' : 'few') + '" role="group" aria-label="' + esc(q.title) + '">' + opts + '</div>';
      if (q.type === 'multi') {
        var n = (ans || []).length;
        body += '<div class="bs-actions"><button type="button" class="btn btn--primary c-btn c-btn--primary bs-next" data-bs-next' + (n ? '' : ' disabled') + '>Volgende</button>' +
          '<span class="bs-count">' + (q.max ? n + ' van max. ' + q.max + ' gekozen' : '') + '</span></div>';
      }
    }

    this.view(
      '<div class="bs-question" data-q="' + q.id + '">' +
        '<div class="bs-progress" aria-hidden="true"><span style="width:' + pct + '%"></span></div>' +
        '<div class="bs-question__top"><button type="button" class="bs-link bs-back" data-bs-back>← Terug</button>' +
        '<span class="bs-step">' + esc(this.route().sub) + ' · vraag ' + (this.state.step + 1) + ' van ' + total + '</span>' +
        '<button type="button" class="bs-link bs-back" data-bs-restart>Opnieuw ↺</button></div>' +
        '<div class="bs-ut-pill-wrap" data-bs-ut-pill>' + this.utPillHtml() + '</div>' +
        '<h2 class="bs-question__title" tabindex="-1" data-bs-focus>' + esc(q.title) + '</h2>' +
        (q.help ? '<p class="bs-question__help">' + esc(q.help) + '</p>' : '') +
        body +
      '</div>'
    );
  };

  Sommelier.prototype.untappdHtml = function (q) {
    var ut = this.ut;
    var st = utState();
    var name = ut.username || st.username || '';
    var excl = this.state.answers.excludeTasted !== false;
    var status = '';
    if (ut.status === 'busy') status = '<p class="bs-ut__status is-busy" role="status"><span class="bs-dot"></span> ' + (ut.count ? ut.count + ' bieren gevonden, we zoeken nog verder…' : 'Profiel ophalen…') + ' Je kunt alvast verder.</p>';
    else if (ut.status === 'done') status = '<p class="bs-ut__status is-ok" role="status">✓ ' + ut.count + ' gedronken bieren geladen' + (name ? ' voor <strong>' + esc(name) + '</strong>' : '') + '.</p>';
    else if (ut.status === 'error') status = '<p class="bs-ut__status is-error" role="status">' + esc(ut.text || 'Kon je profiel niet ophalen.') + '</p>';

    return '<div class="bs-ut">' +
      '<p class="bs-ut__brand"><span class="bs-ut__logo" aria-hidden="true">★</span>Untappd</p>' +
      '<form class="bs-ut__form" data-bs-ut-form novalidate>' +
        '<label class="visually-hidden" for="bs-ut-name">Untappd-gebruikersnaam</label>' +
        '<input id="bs-ut-name" class="bs-input" name="username" type="text" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="Je Untappd-gebruikersnaam" value="' + esc(name) + '">' +
        '<button type="submit" class="btn btn--secondary c-btn c-btn--hollow bs-ut__btn"' + (ut.status === 'busy' ? ' disabled' : '') + '>' + (ut.status === 'done' ? 'Opnieuw ophalen' : 'Koppelen') + '</button>' +
      '</form>' +
      status +
      '<label class="bs-toggle"><input type="checkbox" data-bs-exclude' + (excl ? ' checked' : '') + '> <span>Laat bieren weg die ik al gedronken heb</span></label>' +
      '<details class="bs-ut__more"><summary>Profiel op privé of heel veel check-ins? Upload je Untappd-export</summary>' +
        '<p>Untappd Insiders kunnen hun volledige historie downloaden via <em>Account → Download history</em> (CSV of JSON). Het bestand blijft op je eigen apparaat, wij lezen alleen de bier-ID\'s, stijlen en je scores.</p>' +
        '<input type="file" accept=".csv,.json,application/json,text/csv" data-bs-export>' +
      '</details>' +
      '<p class="bs-ut__note">We kijken alleen naar je openbare Untappd-profiel. Er wordt niets opgeslagen bij je bestelling.</p>' +
    '</div>' +
      '<div class="bs-actions">' +
        '<button type="button" class="btn btn--primary c-btn c-btn--primary bs-next" data-bs-next>' + (ut.status === 'idle' ? (q.optional ? 'Geen Untappd, verder' : 'Overslaan') : 'Volgende') + '</button>' +
      '</div>';
  };

  Sommelier.prototype.refreshUntappd = function () {
    var q = this.root.querySelector('.bs-question[data-q="untappd"]');
    if (q) {
      var input = q.querySelector('#bs-ut-name');
      var val = input ? input.value : '';
      var holder = q.querySelector('.bs-ut');
      var actions = holder.nextElementSibling;
      if (actions && actions.classList.contains('bs-actions')) actions.remove();
      holder.outerHTML = this.untappdHtml(this.visibleQuestions()[this.state.step]);
      var ni = q.querySelector('#bs-ut-name'); if (ni && val) ni.value = val;
    }
    var badge = this.root.querySelector('[data-bs-ut-badge]');
    if (badge) badge.innerHTML = this.utBadgeHtml();
    var pill = this.root.querySelector('[data-bs-ut-pill]');
    if (pill) pill.innerHTML = this.utPillHtml();
  };

  /** Altijd zichtbaar statuslampje: oranje = bezig, groen = gekoppeld, rood = mislukt. */
  Sommelier.prototype.utPillHtml = function () {
    var ut = this.ut;
    if (ut.status === 'busy') return '<span class="bs-ut-pill is-busy"><span class="bs-lamp"></span>Untappd wordt geladen' + (ut.count ? ' · ' + ut.count + ' bieren gevonden' : '') + '</span>';
    if (ut.status === 'done') return '<span class="bs-ut-pill is-ok"><span class="bs-lamp"></span>Untappd gekoppeld' + (ut.username ? ' (' + esc(ut.username) + ')' : '') + ' · ' + ut.count + ' bieren</span>';
    if (ut.status === 'error') return '<span class="bs-ut-pill is-error"><span class="bs-lamp"></span>Untappd niet gekoppeld</span>';
    return '';
  };

  Sommelier.prototype.utBadgeHtml = function () {
    if (this.ut.status === 'busy') return this.utPillHtml() + '<span class="bs-ut-note">Bieren die je al gedronken hebt, worden automatisch vervangen zodra alles binnen is.</span>';
    if (this.ut.status === 'done') return this.utPillHtml();
    return '';
  };

  Sommelier.prototype.startUntappd = function (username) {
    var self = this;
    this.ut = { status: 'busy', count: 0, username: username };
    this.refreshUntappd();
    utFetchTaste(username).then(function (st) { self.untappdStyles = st || {}; });
    utConnect(username, function (prog) {
      self.ut.count = prog.count;
      self.tasted = new Set(utState().beerIds.map(String));
      self.refreshUntappd();
      // Staat het advies al op het scherm? Vervang direct bieren die inmiddels als gedronken bekend zijn.
      if (self.root.querySelector('.bs-result') && self.state.answers.excludeTasted !== false &&
          self.selection.some(function (c) { return c.p.bid && self.tasted.has(c.p.bid); })) {
        self.renderResult(true);
      }
    }).then(function (s) {
      self.tasted = new Set(s.beerIds.map(String));
      self.ut = { status: 'done', count: s.beerIds.length, username: s.username };
      self.refreshUntappd();
      if (self.root.querySelector('.bs-result')) self.renderResult(true);
    }).catch(function (err) {
      var map = {
        'invalid-username': 'Dat lijkt geen geldige Untappd-gebruikersnaam.',
        'not-found': 'Die gebruiker kunnen we niet vinden op Untappd.',
        'private': 'Dit Untappd-profiel staat op privé. Upload eventueel je export hieronder.',
        'timeout': 'Untappd reageert traag. Probeer het later nog eens of ga gewoon verder.',
        'unavailable': 'De Untappd-koppeling is nu niet beschikbaar.'
      };
      var msg = (err && map[err.message]) || (err && err.message && err.message.length < 140 ? err.message : 'Kon je profiel niet ophalen. Je kunt gewoon verder.');
      self.ut = { status: 'error', count: 0, text: msg, username: username };
      self.refreshUntappd();
    });
  };

  Sommelier.prototype.buildProfile = function () {
    var level = this.state.level;
    var prof = newProfile(level);
    var answers = this.state.answers;
    this.visibleQuestions().forEach(function (q) {
      if (!q.options) return;
      var a = answers[q.id];
      var vals = Array.isArray(a) ? a : (a != null ? [a] : []);
      q.options.forEach(function (o) { if (vals.indexOf(o.v) !== -1 && o.fx) o.fx(prof); });
    });
    prof.excludeTasted = answers.excludeTasted !== false;
    if (this.route().finalize) this.route().finalize(prof, answers);
    // Untappd-smaak telt zwaarder bij kenners
    var w = level === 'kenner' ? 1 : level === 'liefhebber' ? .7 : .4;
    for (var k in this.untappdStyles) prof.untappdStyles[k] = this.untappdStyles[k] * w;
    return prof;
  };

  Sommelier.prototype.renderResult = function (keepSelection) {
    var self = this;
    if (!this.products) {
      if (this.catalogError) {
        this.view('<div class="bs-result bs-error"><h2 class="bs-question__title" tabindex="-1" data-bs-focus>Oeps</h2><p>We konden het assortiment niet laden. Vernieuw de pagina en probeer het opnieuw.</p></div>');
        return;
      }
      this.view('<div class="bs-loading" role="status"><span class="bs-glass" aria-hidden="true"></span><p tabindex="-1" data-bs-focus>De sommelier loopt de kelder door…</p></div>');
      this.catalogPromise.then(function () { self.renderResult(); }, function () { self.renderResult(); });
      return;
    }
    var prof = this.buildProfile();
    this.profile = prof;
    this.ranked = scoreAll(this.products, prof, this.tasted, this.state.seed);
    if (!keepSelection || !this.selection.length) this.selection = pick(this.ranked, prof.count, prof);
    else {
      // na Untappd-update: inmiddels gedronken bieren vervangen
      var keep = this.selection.filter(function (c) { return !(prof.excludeTasted && c.p.bid && self.tasted.has(c.p.bid)); });
      this.selection = pick(this.ranked, prof.count, prof, keep);
    }
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ level: this.state.level, answers: this.state.answers, seed: this.state.seed })); } catch (e) { /* */ }
    if (prof.group) return this.renderGroupResult(prof);

    var per = persona(prof, this.selection);
    var skipped = 0;
    if (this.tasted.size) this.products.forEach(function (p) { if (p.bid && self.tasted.has(p.bid)) skipped++; });

    var chips = profileChips(prof).map(function (c) { return '<span class="bs-chip">' + esc(c) + '</span>'; }).join('');

    var cards = this.selection.map(function (c, i) { return self.cardHtml(c, i); }).join('');

    var empty = !this.selection.length
      ? '<div class="bs-empty"><p>Met deze antwoorden vinden we helaas niets op voorraad. Probeer een ruimer budget of minder strenge filters.</p><button type="button" class="btn btn--primary c-btn c-btn--primary" data-bs-edit>Antwoorden aanpassen</button></div>'
      : '';

    this.view(
      '<div class="bs-result">' +
        '<div class="bs-persona"' + (this.cfg.img.result ? ' style="--img:url(\'' + esc(this.cfg.img.result) + '\')"' : '') + '>' +
          '<button type="button" class="bs-persona__restart" data-bs-restart>↺ Opnieuw beginnen</button>' +
          '<p class="bs-eyebrow">Jouw bierprofiel</p>' +
          '<h2 class="bs-title" tabindex="-1" data-bs-focus>' + esc(per.name) + '</h2>' +
          '<p class="bs-lead">' + esc(per.line) + '</p>' +
          (chips ? '<div class="bs-chips">' + chips + '</div>' : '') +
          '<p class="bs-ut-badge" data-bs-ut-badge>' + this.utBadgeHtml() + '</p>' +
          (skipped && prof.excludeTasted ? '<p class="bs-skipped">We hebben ' + skipped + ' bieren uit ons assortiment overgeslagen die je al op Untappd hebt gecheckt.</p>' : '') +
        '</div>' +
        empty +
        '<div class="bs-grid">' + cards + '</div>' +
        (this.selection.length ? this.summaryHtml() : '') +
        '<div class="bs-result__more">' +
          '<button type="button" class="bs-link bs-link--caps" data-bs-more>Nog 3 bieren erbij</button>' +
          '<button type="button" class="bs-link bs-link--caps" data-bs-shuffle>Ander pakket</button>' +
          '<button type="button" class="bs-link bs-link--caps" data-bs-edit>Antwoorden aanpassen</button>' +
          '<button type="button" class="bs-link bs-link--caps" data-bs-restart>Opnieuw beginnen</button>' +
        '</div>' +
      '</div>'
    );
  };

  // ---------------------------------------------------------------------------
  // Proeverij-resultaat
  // ---------------------------------------------------------------------------
  Sommelier.prototype.groupLabels = function () {
    var a = this.state.answers, r = ROUTES.proeverij;
    function lab(id) { var q = r.questions.filter(function (x) { return x.id === id; })[0]; var o = q && q.options.filter(function (x) { return x.v === a[id]; })[0]; return o ? o.label : ''; }
    return { publiek: lab('publiek'), thema: lab('thema') };
  };

  Sommelier.prototype.renderGroupResult = function (prof) {
    var self = this;
    this.selection.sort(tastingSort);
    var labels = this.groupLabels();
    var chips = [labels.thema, labels.publiek, POUR_CL + ' cl p.p. per bier', liters(prof.group * POUR_CL) + ' liter per soort']
      .filter(Boolean).map(function (c) { return '<span class="bs-chip">' + esc(c) + '</span>'; }).join('');
    var empty = !this.selection.length
      ? '<div class="bs-empty"><p>Voor ' + prof.group + ' personen hebben we met deze keuzes niet genoeg op voorraad. Kies een ander thema of minder bieren, of neem contact met ons op voor grote groepen.</p><button type="button" class="btn btn--primary c-btn c-btn--primary" data-bs-edit>Antwoorden aanpassen</button></div>'
      : '';
    var short = this.selection.length && this.selection.length < prof.count
      ? '<p class="bs-group-note">We vonden ' + this.selection.length + ' van de ' + prof.count + ' bieren met genoeg voorraad voor ' + prof.group + ' personen.</p>' : '';
    this.view(
      '<div class="bs-result bs-result--group">' +
        '<div class="bs-persona"' + (this.cfg.img.proeverij || this.cfg.img.result ? ' style="--img:url(\'' + esc(this.cfg.img.proeverij || this.cfg.img.result) + '\')"' : '') + '>' +
          '<button type="button" class="bs-persona__restart" data-bs-restart>↺ Opnieuw beginnen</button>' +
          '<p class="bs-eyebrow">Jouw proeverij</p>' +
          '<h2 class="bs-title" tabindex="-1" data-bs-focus>Proeverij voor ' + prof.group + ' personen</h2>' +
          '<p class="bs-lead">' + this.selection.length + ' bieren in proefvolgorde, van licht naar zwaar. Per soort hebben we uitgerekend hoeveel je nodig hebt.</p>' +
          '<div class="bs-chips">' + chips + '</div>' +
        '</div>' +
        empty + short +
        '<ol class="bs-grid bs-grid--group">' + this.selection.map(function (c, i) { return self.groupCardHtml(c, i); }).join('') + '</ol>' +
        (this.selection.length ? this.groupSummaryHtml() : '') +
        '<div class="bs-result__more">' +
          (this.selection.length < MAX_KINDS ? '<button type="button" class="bs-link bs-link--caps" data-bs-more>Nog een bier erbij</button>' : '') +
          '<button type="button" class="bs-link bs-link--caps" data-bs-print>Proefnotities met deze bieren</button>' +
          '<button type="button" class="bs-link bs-link--caps" data-bs-shuffle>Andere bieren</button>' +
          '<button type="button" class="bs-link bs-link--caps" data-bs-edit>Antwoorden aanpassen</button>' +
        '</div>' +
      '</div>'
    );
  };

  Sommelier.prototype.groupCardHtml = function (c, i) {
    var p = c.p, prof = this.profile;
    var n = unitsFor(p, prof);
    var meta = [p.typeLabel, abvLabel(p.abv), clLabel(p.cl)].filter(Boolean).join(' · ');
    var desc = shortDesc(p.desc, 170);
    return '<li class="bs-card bs-card--group" data-idx="' + i + '" style="--beer:' + p.color + '">' +
      '<a class="bs-card__media" href="/products/' + esc(p.handle) + '" tabindex="-1" aria-hidden="true">' +
        (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="240" height="240">' : '<span class="bs-card__ph"></span>') +
        '<span class="bs-card__no">' + (i + 1) + '</span>' +
      '</a>' +
      '<div class="bs-card__body">' +
        '<p class="bs-card__vendor">' + esc(p.vendor) + '</p>' +
        '<h3 class="bs-card__title"><a href="/products/' + esc(p.handle) + '">' + esc(p.title) + '</a></h3>' +
        '<p class="bs-card__meta">' + esc(meta) + '</p>' +
        (desc ? '<p class="bs-card__desc">' + esc(desc) + '</p>' : '') +
        '<p class="bs-card__qty"><strong>' + n + ' ×</strong> ' + esc(clLabel(p.cl) || 'per stuk') + ' = ' + liters(n * (p.cl || 33)) + ' liter <span>(' + p.qty + ' op voorraad)</span></p>' +
        '<div class="bs-card__foot">' +
          '<span class="bs-card__price">' + money(p.price * n) + '<small>' + n + ' × ' + money(p.price) + '</small></span>' +
          '<span class="bs-card__btns">' +
            '<button type="button" class="bs-icon-btn" data-bs-swap="' + i + '" title="Ander bier" aria-label="Wissel ' + esc(p.title) + ' voor een ander bier">↻</button>' +
            '<button type="button" class="bs-icon-btn" data-bs-remove="' + i + '" title="Verwijderen" aria-label="Verwijder ' + esc(p.title) + '">✕</button>' +
          '</span>' +
        '</div>' +
      '</div>' +
    '</li>';
  };

  Sommelier.prototype.groupTotals = function () {
    var prof = this.profile, g = this.cfg.group;
    var sub = 0, dep = 0, units = 0, allMin = true;
    this.selection.forEach(function (c) {
      var n = unitsFor(c.p, prof);
      sub += c.p.price * n; dep += (c.p.deposit || 0) * n; units += n;
      if (n < MIN_PER_KIND) allMin = false;
    });
    var eligible = this.selection.length >= 4 && allMin;
    var disc = eligible ? Math.round(sub * g.pct / 100) : 0;
    var notes = prof.printNotes && g.notesVariant ? g.notesPrice * prof.group : 0;
    return { sub: sub, dep: dep, units: units, eligible: eligible, disc: disc, notes: notes, total: sub - disc + notes, credit: g.credit * prof.group };
  };

  Sommelier.prototype.groupSummaryHtml = function () {
    var t = this.groupTotals(), g = this.cfg.group, prof = this.profile, n = this.selection.length;
    var lines = [];
    lines.push(t.eligible
      ? '<span class="bs-sum-line is-good">' + g.pct + '% proeverijkorting: − ' + money(t.disc) + ' <em>(code ' + esc(g.code) + ' gaat automatisch mee)</em></span>'
      : '<span class="bs-sum-line">Kies minimaal 4 bieren voor ' + g.pct + '% proeverijkorting.</span>');
    if (t.notes) lines.push('<span class="bs-sum-line">Gedrukte proefnotities: ' + prof.group + ' × ' + money(g.notesPrice) + '</span>');
    lines.push('<span class="bs-sum-line is-gold">Tegoed: ' + money(t.credit) + ' (' + prof.group + ' × ' + money(g.credit) + ') op een proeverij in Arnhem of Grave</span>');
    return '<div class="bs-summary bs-summary--group" data-bs-summary>' +
      '<div class="bs-summary__info">' +
        '<p class="bs-summary__total"><strong>' + n + ' bieren · ' + t.units + ' stuks · ' + money(t.total) + '</strong>' +
          (t.disc ? ' <s class="bs-summary__was">' + money(t.sub + t.notes) + '</s>' : '') +
          (t.dep ? ' <span class="bs-summary__dep">+ ' + money(t.dep) + ' statiegeld</span>' : '') + '</p>' +
        '<p class="bs-summary__lines">' + lines.join('') + '</p>' +
      '</div>' +
      '<div class="bs-summary__btns">' +
        '<button type="button" class="btn btn--primary c-btn c-btn--primary bs-summary__cta" data-bs-addall>Alles in winkelwagen</button>' +
        '<button type="button" class="bs-summary__print" data-bs-print>Proefnotities printen</button>' +
      '</div>' +
    '</div>';
  };

  Sommelier.prototype.cardHtml = function (c, i) {
    if (this.profile && this.profile.group) return this.groupCardHtml(c, i);
    var p = c.p;
    var meta = [p.typeLabel, abvLabel(p.abv), clLabel(p.cl)].filter(Boolean).join(' · ');
    var why = c.why.length ? c.why : ['Past bij je profiel'];
    return '<article class="bs-card" data-idx="' + i + '" style="--beer:' + p.color + '">' +
      '<a class="bs-card__media" href="/products/' + esc(p.handle) + '" tabindex="-1" aria-hidden="true">' +
        (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="240" height="240">' : '<span class="bs-card__ph"></span>') +
        (p.rating
          ? '<span class="bs-card__rating"><span class="bs-stars" style="--r:' + p.rating.toFixed(2) + '" aria-hidden="true"></span>Untappd ' + p.rating.toFixed(2) + ' / 5</span>'
          // Geen score in de feed: laat assets/untappd-rating.js hem live ophalen, net als op de productkaarten
          : (p.url ? '<span class="bs-card__rating js-untappd-rating" data-untappd-url="' + esc(p.url) + '" hidden><span class="bs-stars" data-untappd-rating-star aria-hidden="true"></span><span data-untappd-rating-text></span></span>' : '')) +
        (p.isNew ? '<span class="bs-card__new">New</span>' : '') +
      '</a>' +
      '<div class="bs-card__body">' +
        '<p class="bs-card__vendor">' + esc(p.vendor) + '</p>' +
        '<h3 class="bs-card__title"><a href="/products/' + esc(p.handle) + '">' + esc(p.title) + '</a></h3>' +
        '<p class="bs-card__meta">' + esc(meta) + '</p>' +
        '<ul class="bs-card__why">' + why.map(function (w) { return '<li>' + esc(w) + '</li>'; }).join('') + '</ul>' +
        '<div class="bs-card__foot">' +
          '<span class="bs-card__price">' + money(p.price) + '</span>' +
          '<span class="bs-card__btns">' +
            '<button type="button" class="bs-icon-btn" data-bs-swap="' + i + '" title="Ander bier" aria-label="Wissel ' + esc(p.title) + ' voor een ander bier">↻</button>' +
            '<button type="button" class="bs-icon-btn" data-bs-remove="' + i + '" title="Verwijderen" aria-label="Verwijder ' + esc(p.title) + '">✕</button>' +
          '</span>' +
        '</div>' +
      '</div>' +
    '</article>';
  };

  Sommelier.prototype.summaryHtml = function () {
    if (this.profile && this.profile.group) return this.groupSummaryHtml();
    var total = 0, dep = 0;
    this.selection.forEach(function (c) { total += c.p.price; dep += c.p.deposit || 0; });
    var n = this.selection.length;
    var line, pct;
    if (total < this.cfg.minOrder) {
      line = 'Nog ' + money(this.cfg.minOrder - total) + ' tot de minimale bestelwaarde van ' + money(this.cfg.minOrder);
      pct = total / this.cfg.freeShipping * 100;
    } else if (total < this.cfg.freeShipping) {
      line = 'Nog ' + money(this.cfg.freeShipping - total) + ' tot gratis verzending';
      pct = total / this.cfg.freeShipping * 100;
    } else {
      line = 'Je pakket wordt gratis verzonden';
      pct = 100;
    }
    return '<div class="bs-summary" data-bs-summary>' +
      '<div class="bs-summary__info">' +
        '<p class="bs-summary__total"><strong>' + n + ' ' + (n === 1 ? 'bier' : 'bieren') + ' · ' + money(total) + '</strong>' + (dep ? ' <span class="bs-summary__dep">+ ' + money(dep) + ' statiegeld</span>' : '') + '</p>' +
        '<div class="bs-bar" aria-hidden="true"><span style="width:' + Math.min(100, pct).toFixed(1) + '%"></span></div>' +
        '<p class="bs-summary__line">' + esc(line) + '</p>' +
      '</div>' +
      '<button type="button" class="btn btn--primary c-btn c-btn--primary bs-summary__cta" data-bs-addall>Alles in winkelwagen</button>' +
    '</div>';
  };

  function profileChips(prof) {
    var chips = [];
    Object.keys(prof.fam).filter(function (k) { return prof.fam[k] >= 1 && FAMILIES[k]; })
      .sort(function (a, b) { return prof.fam[b] - prof.fam[a]; }).slice(0, 3)
      .forEach(function (k) { chips.push(FAMILIES[k].label); });
    if (prof.flavor.bitter <= -1) chips.push('Niet te bitter');
    if (prof.flavor.zuur <= -1) chips.push('Niet zuur');
    if (prof.barrel === 'ja') chips.push('Barrel aged');
    if (prof.barrel === 'nee') chips.push('Zonder vaten');
    if (prof.abvMax && !prof.abvMin) chips.push('Tot ' + Math.floor(prof.abvMax) + '%');
    if (prof.abvMin && prof.abvMax) chips.push(Math.ceil(prof.abvMin) + '–' + Math.floor(prof.abvMax) + '%');
    if (prof.abvMin && !prof.abvMax) chips.push(Math.ceil(prof.abvMin) + '%+');
    if (prof.minRating) chips.push('Untappd ' + String(prof.minRating).replace('.', ',') + '+');
    if (prof.vintage === 'oud') chips.push('Gerijpt');
    if (prof.vintage === 'vers') chips.push('Vers');
    if (prof.priceMax) chips.push('Max. ' + money(prof.priceMax) + ' per bier');
    return chips;
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------
  Sommelier.prototype.onClick = function (e) {
    var t = e.target.closest('button, a');
    if (!t || !this.root.contains(t)) return;
    var self = this;

    if (t.hasAttribute('data-bs-level')) {
      this.state = { level: t.getAttribute('data-bs-level'), step: 0, answers: {}, seed: (Date.now() % 100000) | 0 };
      this.selection = [];
      return this.renderQuestion();
    }
    if (t.hasAttribute('data-bs-resume')) {
      try {
        var last = JSON.parse(localStorage.getItem(STORE_KEY));
        this.state = { level: last.level, step: 999, answers: last.answers || {}, seed: last.seed || 1 };
        this.selection = [];
        return this.renderResult();
      } catch (err) { return this.renderIntro(); }
    }
    if (t.hasAttribute('data-bs-back')) {
      if (this.state.step === 0) return this.renderIntro();
      this.state.step--;
      return this.renderQuestion();
    }
    if (t.hasAttribute('data-bs-option')) {
      var q = this.visibleQuestions()[this.state.step];
      var v = t.getAttribute('data-bs-option');
      var opt = q.options.filter(function (o) { return o.v === v; })[0];
      if (q.type === 'multi') {
        var cur = (this.state.answers[q.id] || []).slice();
        var idx = cur.indexOf(v);
        if (idx !== -1) cur.splice(idx, 1);
        else {
          if (opt && opt.exclusive) cur = [v];
          else {
            cur = cur.filter(function (x) { var o = q.options.filter(function (oo) { return oo.v === x; })[0]; return !(o && o.exclusive); });
            if (q.max && cur.length >= q.max) cur.shift();
            cur.push(v);
          }
        }
        this.state.answers[q.id] = cur;
        // Alleen de knoppen bijwerken, niet het hele scherm
        this.root.querySelectorAll('[data-bs-option]').forEach(function (b) {
          var on = cur.indexOf(b.getAttribute('data-bs-option')) !== -1;
          b.classList.toggle('is-selected', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        var next = this.root.querySelector('[data-bs-next]'); if (next) next.disabled = !cur.length;
        var cnt = this.root.querySelector('.bs-count'); if (cnt && q.max) cnt.textContent = cur.length + ' van max. ' + q.max + ' gekozen';
        return;
      }
      this.state.answers[q.id] = v;
      t.classList.add('is-selected');
      setTimeout(function () { self.state.step++; self.renderQuestion(); }, 160);
      return;
    }
    if (t.hasAttribute('data-bs-num')) {
      var inp = this.root.querySelector('#bs-num');
      if (inp) { inp.value = clampGroup(Number(inp.value) + Number(t.getAttribute('data-bs-num'))); this.onInput({ target: inp }); }
      return;
    }
    if (t.hasAttribute('data-bs-next')) {
      this.saveNumber();
      this.state.step++;
      return this.renderQuestion();
    }
    if (t.hasAttribute('data-bs-blank')) return openNotes(null, { slots: 6 });
    if (t.hasAttribute('data-bs-print')) {
      var pr = this.profile;
      return openNotes(this.selection.map(function (c) { return c.p; }), {
        group: pr.group, thema: this.groupLabels().thema, credit: this.cfg.group.credit
      });
    }
    if (t.hasAttribute('data-bs-swap')) {
      var i = Number(t.getAttribute('data-bs-swap'));
      var old = this.selection[i];
      var rest = this.selection.filter(function (_, j) { return j !== i; });
      this.excluded = this.excluded || {};
      this.excluded[old.p.id] = 1;
      var ex = this.excluded;
      var pool = this.ranked.filter(function (c) { return !ex[c.p.id]; });
      // voorkeur voor hetzelfde soort bier
      var same = pool.filter(function (c) { return c.p.fam === old.p.fam; });
      var replacement = pick(same.length ? same : pool, rest.length + 1, this.profile, rest).slice(rest.length)[0];
      if (replacement) this.selection.splice(i, 1, replacement);
      return this.rerenderCards();
    }
    if (t.hasAttribute('data-bs-remove')) {
      var ri = Number(t.getAttribute('data-bs-remove'));
      this.excluded = this.excluded || {};
      this.excluded[this.selection[ri].p.id] = 1;
      this.selection.splice(ri, 1);
      return this.rerenderCards();
    }
    if (t.hasAttribute('data-bs-more')) {
      var ex2 = this.excluded || {};
      var add = this.profile.group ? 1 : 3;
      if (this.profile.group && this.selection.length >= MAX_KINDS) return;
      this.selection = pick(this.ranked.filter(function (c) { return !ex2[c.p.id]; }), this.selection.length + add, this.profile, this.selection);
      return this.profile.group ? this.renderGroupResult(this.profile) : this.rerenderCards();
    }
    if (t.hasAttribute('data-bs-shuffle')) {
      this.state.seed = (this.state.seed * 7 + 13) % 100000;
      this.excluded = {};
      this.selection = [];
      return this.renderResult();
    }
    if (t.hasAttribute('data-bs-edit')) {
      this.state.step = 0;
      this.selection = [];
      return this.renderQuestion();
    }
    if (t.hasAttribute('data-bs-restart')) {
      this.selection = []; this.excluded = {};
      return this.renderIntro();
    }
    if (t.hasAttribute('data-bs-addall')) {
      if (!this.selection.length) return;
      t.disabled = true;
      var label = t.textContent;
      t.textContent = 'Bezig met toevoegen…';
      var old = self.root.querySelectorAll('.bs-summary__done, .bs-summary__err');
      old.forEach(function (n) { n.remove(); });
      var prof = this.profile, g = this.cfg.group, qtys = null, extra = null;
      if (prof.group) {
        qtys = {};
        this.selection.forEach(function (c) { qtys[c.p.id] = unitsFor(c.p, prof); });
        var tot = this.groupTotals();
        extra = function () {
          var steps = Promise.resolve();
          if (prof.printNotes && g.notesVariant) {
            steps = postJson('cart/add.js', { items: [{ id: Number(g.notesVariant), quantity: prof.group, properties: { _bron: 'biersommelier', _sommelier_route: 'proeverij' } }] }).catch(function () {});
          }
          return steps.then(function () {
            var attrs = {
              'Proeverij': prof.group + ' personen · ' + self.selection.length + ' bieren',
              'Proeftegoed': money(tot.credit) + ' (' + prof.group + ' × ' + money(g.credit) + ') op een proeverij in Arnhem of Grave'
            };
            return tot.eligible ? applyGroupExtras(g.code, attrs) : postJson('cart/update.js', { attributes: attrs }).catch(function () {});
          });
        };
      }
      addToCart(this.selection.map(function (c) { return c.p; }), this.state.level, qtys, extra).then(function (res) {
        t.textContent = '✓ Toegevoegd';
        var sum = self.root.querySelector('[data-bs-summary]');
        if (sum) {
          var msg = res.failed.length
            ? res.added + ' van de ' + (res.added + res.failed.length) + ' bieren staan in je winkelwagen. Niet meer beschikbaar: ' + esc(res.failed.join(', ')) + '.'
            : (prof.group ? 'Je proeverij staat in je winkelwagen' + (self.groupTotals().eligible ? ', inclusief ' + g.pct + '% korting.' : '.') : 'Je pakket staat in je winkelwagen.');
          sum.insertAdjacentHTML('beforeend', '<p class="bs-summary__done">' + msg + ' <a href="/cart">Naar winkelwagen →</a></p>');
        }
        if (res.synced) openCart();
        setTimeout(function () { t.disabled = false; t.textContent = label; }, 4000);
      }).catch(function (err) {
        t.disabled = false; t.textContent = label;
        var sum = self.root.querySelector('[data-bs-summary]');
        if (sum) sum.insertAdjacentHTML('beforeend', '<p class="bs-summary__err">Toevoegen lukte niet' + (err && err.message ? ': ' + esc(err.message) : '') + '. Probeer het opnieuw of voeg de bieren los toe.</p>');
      });
    }
  };

  Sommelier.prototype.rerenderCards = function () {
    var self = this;
    var grid = this.root.querySelector('.bs-grid');
    if (!grid) return this.renderResult(true);
    if (this.profile && this.profile.group) this.selection.sort(tastingSort);
    grid.innerHTML = this.selection.map(function (c, i) { return self.cardHtml(c, i); }).join('');
    var sum = this.root.querySelector('[data-bs-summary]');
    var html = this.selection.length ? this.summaryHtml() : '';
    if (sum) sum.outerHTML = html || '<div data-bs-summary></div>';
    else if (html) grid.insertAdjacentHTML('afterend', html);
  };

  Sommelier.prototype.onChange = function (e) {
    var t = e.target;
    if (t.hasAttribute('data-bs-exclude')) {
      this.state.answers.excludeTasted = t.checked;
      return;
    }
    if (t.hasAttribute('data-bs-export')) {
      var file = t.files && t.files[0];
      if (!file) return;
      var self = this;
      file.text().then(function (txt) {
        var res = parseExport(txt);
        if (!res.ids.length) throw new Error('Geen bieren gevonden in dit bestand.');
        var s = utState();
        var merged = new Set((s.beerIds || []).concat(res.ids));
        s.beerIds = Array.from(merged); s.fetchedAt = Date.now();
        if (!s.username) s.username = 'export';
        utSave(s);
        self.tasted = merged;
        if (Object.keys(res.styles).length) self.untappdStyles = res.styles;
        self.ut = { status: 'done', count: merged.size, username: s.username === 'export' ? '' : s.username };
        self.refreshUntappd();
        if (window.__untappdUserFilter && window.__untappdUserFilter.applyToAll) window.__untappdUserFilter.applyToAll();
      }).catch(function (err) {
        self.ut = { status: 'error', text: 'Kon het bestand niet lezen: ' + (err && err.message || 'onbekend formaat') };
        self.refreshUntappd();
      });
    }
  };

  Sommelier.prototype.onInput = function (e) {
    var t = e.target;
    if (t && t.id === 'bs-num') {
      var calc = this.root.querySelector('[data-bs-num-calc]');
      var v = Number(t.value);
      if (calc && v) calc.innerHTML = groupCalc(clampGroup(v));
    }
  };

  Sommelier.prototype.saveNumber = function () {
    var inp = this.root.querySelector('#bs-num');
    var q = this.visibleQuestions()[this.state.step];
    if (inp && q && q.type === 'number') this.state.answers[q.id] = String(clampGroup(inp.value));
  };

  Sommelier.prototype.onSubmit = function (e) {
    var form = e.target;
    if (form.hasAttribute('data-bs-num-form')) {
      e.preventDefault();
      this.saveNumber();
      this.state.step++;
      return this.renderQuestion();
    }
    if (!form.hasAttribute('data-bs-ut-form')) return;
    e.preventDefault();
    var name = form.querySelector('input[name="username"]').value.trim();
    if (!name) {
      this.ut = { status: 'error', text: 'Vul je Untappd-gebruikersnaam in.' };
      return this.refreshUntappd();
    }
    this.startUntappd(name);
  };

  // ===========================================================================
  // Init
  // ===========================================================================
  function init() {
    document.querySelectorAll('[data-biersommelier]').forEach(function (el) {
      if (el.__bs) return;
      el.__bs = new Sommelier(el);
    });
  }

  // Exporteren voor tests
  window.__biersommelier = { ROUTES: ROUTES, STYLES: STYLES, normalize: normalize, scoreAll: scoreAll, pick: pick, newProfile: newProfile, parseExport: parseExport, mapUntappdStyle: mapUntappdStyle, persona: persona, init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
